var __dispose = Symbol.dispose || /* @__PURE__ */ Symbol.for("Symbol.dispose");
var __asyncDispose = Symbol.asyncDispose || /* @__PURE__ */ Symbol.for("Symbol.asyncDispose");
var __using = (stack, value, async) => {
  if (value != null) {
    if (typeof value !== "object" && typeof value !== "function")
      throw TypeError('Object expected to be assigned to "using" declaration');
    var dispose;
    if (async)
      dispose = value[__asyncDispose];
    if (dispose === undefined)
      dispose = value[__dispose];
    if (typeof dispose !== "function")
      throw TypeError("Object not disposable");
    stack.push([async, dispose, value]);
  } else if (async) {
    stack.push([async]);
  }
  return value;
};
var __callDispose = (stack, error, hasError) => {
  var E = typeof SuppressedError === "function" ? SuppressedError : function(e, s, m, _) {
    return _ = Error(m), _.name = "SuppressedError", _.error = e, _.suppressed = s, _;
  }, fail = (e) => error = hasError ? new E(e, error, "An error was suppressed during disposal") : (hasError = true, e), next = (it) => {
    while (it = stack.pop()) {
      try {
        var result = it[1] && it[1].call(it[2]);
        if (it[0])
          return Promise.resolve(result).then(next, (e) => (fail(e), next()));
      } catch (e) {
        fail(e);
      }
    }
    if (hasError)
      throw error;
  };
  return next();
};

// src/audio/audio.tsx
import { Internals as Internals19, useRemotionEnvironment as useRemotionEnvironment2 } from "remotion";

// src/audio/audio-for-preview.tsx
import { useContext as useContext3, useEffect as useEffect2, useMemo as useMemo2, useRef, useState as useState2 } from "react";
import {
  Internals as Internals10,
  Audio as RemotionAudio,
  useBufferState,
  useCurrentFrame as useCurrentFrame2,
  useVideoConfig as useVideoConfig2
} from "remotion";

// src/get-time-in-seconds.ts
import { Internals } from "remotion";
var getTimeInSeconds = ({
  loop,
  mediaDurationInSeconds,
  unloopedTimeInSeconds,
  src,
  trimAfter,
  trimBefore,
  fps,
  playbackRate,
  ifNoMediaDuration
}) => {
  if (mediaDurationInSeconds === null && loop && ifNoMediaDuration === "fail") {
    throw new Error(`Could not determine duration of ${src}, but "loop" was set.`);
  }
  const loopDuration = loop ? Internals.calculateMediaDuration({
    trimAfter,
    mediaDurationInFrames: mediaDurationInSeconds ? mediaDurationInSeconds * fps : Infinity,
    playbackRate: 1,
    trimBefore
  }) / fps : Infinity;
  const timeInSeconds = unloopedTimeInSeconds * playbackRate % loopDuration;
  if ((trimAfter ?? null) !== null && !loop) {
    const time = (trimAfter - (trimBefore ?? 0)) / fps;
    if (timeInSeconds >= time) {
      return null;
    }
  }
  return timeInSeconds + (trimBefore ?? 0) / fps;
};
var calculateEndTime = ({
  mediaDurationInSeconds,
  ifNoMediaDuration,
  src,
  trimAfter,
  trimBefore,
  fps
}) => {
  if (mediaDurationInSeconds === null && ifNoMediaDuration === "fail") {
    throw new Error(`Could not determine duration of ${src}, but "loop" was set.`);
  }
  const mediaDuration = Internals.calculateMediaDuration({
    trimAfter,
    mediaDurationInFrames: mediaDurationInSeconds ? mediaDurationInSeconds * fps : Infinity,
    playbackRate: 1,
    trimBefore
  }) / fps;
  return mediaDuration + (trimBefore ?? 0) / fps;
};

// src/media-player.ts
import { ALL_FORMATS, Input, UrlSource } from "mediabunny";
import { Internals as Internals6 } from "remotion";

// src/audio-iterator-manager.ts
import { AudioBufferSink, InputDisposedError } from "mediabunny";
import { Internals as Internals4 } from "remotion";

// src/audio/audio-preview-iterator.ts
import { Internals as Internals3 } from "remotion";

// src/helpers/round-to-4-digits.ts
var roundTo4Digits = (timestamp) => {
  return Math.round(timestamp * 1000) / 1000;
};

// src/set-global-time-anchor.ts
import { Internals as Internals2 } from "remotion";
var ALLOWED_GLOBAL_TIME_ANCHOR_SHIFT = 0.1;
var setGlobalTimeAnchor = ({
  audioContext,
  audioSyncAnchor,
  absoluteTimeInSeconds,
  globalPlaybackRate,
  debugAudioScheduling,
  logLevel
}) => {
  const newAnchor = audioContext.currentTime - absoluteTimeInSeconds / globalPlaybackRate;
  const shift = (newAnchor - audioSyncAnchor.value) * globalPlaybackRate;
  if (Math.abs(shift) < ALLOWED_GLOBAL_TIME_ANCHOR_SHIFT) {
    return;
  }
  if (debugAudioScheduling) {
    Internals2.Log.info({ logLevel, tag: "audio-scheduling" }, "Anchor changed from %s to %s with shift %s", audioSyncAnchor.value, newAnchor, shift);
  }
  audioSyncAnchor.value = newAnchor;
};

// src/audio/audio-preview-iterator.ts
var makeAudioIterator = ({
  startFromSecond,
  maximumTimestamp,
  cache,
  debugAudioScheduling
}) => {
  let destroyed = false;
  const iterator = cache.makeIteratorOrUsePrewarmed(startFromSecond, maximumTimestamp);
  const queuedAudioNodes = [];
  const audioChunksForAfterResuming = [];
  let mostRecentTimestamp = -Infinity;
  let pendingNext = null;
  const cleanupAudioQueue = (audioContext) => {
    for (const node of queuedAudioNodes) {
      try {
        const isAlreadyPlaying = node.scheduledTime - ALLOWED_GLOBAL_TIME_ANCHOR_SHIFT < audioContext.audioContext.currentTime;
        const wasScheduledForThisAnchor = node.scheduledAtAnchor === audioContext.audioSyncAnchor.value;
        if (isAlreadyPlaying && wasScheduledForThisAnchor) {
          continue;
        }
        if (debugAudioScheduling) {
          const currentlyHearing = audioContext.audioContext.getOutputTimestamp().contextTime;
          const nodeEndTime = node.scheduledTime + node.buffer.duration / node.playbackRate;
          Internals3.Log.info({ logLevel: "trace", tag: "audio-scheduling" }, `Stopping node ${node.timestamp.toFixed(3)}, currently hearing = ${currentlyHearing.toFixed(3)} currentTime = ${audioContext.audioContext.currentTime.toFixed(3)} nodeEndTime = ${nodeEndTime.toFixed(3)} scheduledTime = ${node.scheduledTime.toFixed(3)}`);
        }
        node.node.stop();
      } catch {}
    }
    queuedAudioNodes.length = 0;
  };
  const getNextOrNullIfNotAvailable = async () => {
    let next = pendingNext;
    if (!next) {
      next = iterator.next();
    }
    pendingNext = null;
    const result = await Promise.race([
      next,
      new Promise((resolve) => {
        Promise.resolve().then(() => resolve());
      })
    ]);
    if (!result) {
      pendingNext = next;
      return {
        type: "need-to-wait-for-it",
        waitPromise: async () => {
          const res = await next;
          return res.value;
        }
      };
    }
    if (result.value) {
      mostRecentTimestamp = Math.max(mostRecentTimestamp, result.value.timestamp + result.value.duration);
      pendingNext = iterator.next();
      return {
        type: "got-buffer",
        buffer: result.value
      };
    }
    return {
      type: "got-end",
      mostRecentTimestamp
    };
  };
  const tryToSatisfySeek = async (time, onBufferScheduled) => {
    if (time < startFromSecond) {
      return {
        type: "not-satisfied",
        reason: `time requested is before the start of the iterator`
      };
    }
    while (true) {
      const buffer = await getNextOrNullIfNotAvailable();
      if (buffer.type === "need-to-wait-for-it") {
        return {
          type: "not-satisfied",
          reason: "iterator did not have buffer ready"
        };
      }
      if (buffer.type === "got-end") {
        if (time >= mostRecentTimestamp) {
          return {
            type: "ended"
          };
        }
        return {
          type: "not-satisfied",
          reason: `iterator ended before the requested time`
        };
      }
      if (buffer.type === "got-buffer") {
        const bufferTimestamp = roundTo4Digits(buffer.buffer.timestamp);
        const bufferEndTimestamp = roundTo4Digits(buffer.buffer.timestamp + buffer.buffer.duration);
        const timestamp = roundTo4Digits(time);
        if (timestamp < bufferTimestamp) {
          return {
            type: "not-satisfied",
            reason: `iterator is too far, most recently returned ${bufferTimestamp}-${bufferEndTimestamp}, requested ${timestamp}`
          };
        }
        if (bufferTimestamp <= timestamp && bufferEndTimestamp >= timestamp) {
          onBufferScheduled(buffer.buffer);
          return {
            type: "satisfied"
          };
        }
        onBufferScheduled(buffer.buffer);
        continue;
      }
      throw new Error("Unreachable");
    }
  };
  const bufferAsFarAsPossible = async (onBufferScheduled, maxTimestamp) => {
    while (true) {
      if (mostRecentTimestamp >= maxTimestamp) {
        return { type: "max-reached" };
      }
      const buffer = await getNextOrNullIfNotAvailable();
      if (buffer.type === "need-to-wait-for-it") {
        return { type: "waiting" };
      }
      if (buffer.type === "got-end") {
        return { type: "ended" };
      }
      if (buffer.type === "got-buffer") {
        onBufferScheduled(buffer.buffer);
        continue;
      }
      throw new Error("Unreachable");
    }
  };
  const removeAndReturnAllQueuedAudioNodes = () => {
    const nodes = queuedAudioNodes.slice();
    for (const node of nodes) {
      try {
        node.node.stop();
      } catch {}
    }
    queuedAudioNodes.length = 0;
    return nodes;
  };
  const addChunkForAfterResuming = (buffer, timestamp) => {
    audioChunksForAfterResuming.push({
      buffer,
      timestamp
    });
  };
  const moveQueuedChunksToPauseQueue = () => {
    const toQueue = removeAndReturnAllQueuedAudioNodes();
    for (const chunk of toQueue) {
      addChunkForAfterResuming(chunk.buffer, chunk.timestamp);
    }
    if (debugAudioScheduling && toQueue.length > 0) {
      Internals3.Log.trace({ logLevel: "trace", tag: "audio-scheduling" }, `Moved ${toQueue.length} ${toQueue.length === 1 ? "chunk" : "chunks"} to pause queue (${toQueue[0].timestamp.toFixed(3)}-${toQueue[toQueue.length - 1].timestamp + toQueue[toQueue.length - 1].buffer.duration.toFixed(3)})`);
    }
  };
  const getNumberOfChunksAfterResuming = () => {
    return audioChunksForAfterResuming.length;
  };
  return {
    destroy: (audioContext) => {
      cleanupAudioQueue(audioContext);
      destroyed = true;
      iterator.return().catch(() => {
        return;
      });
      audioChunksForAfterResuming.length = 0;
    },
    getNext: async () => {
      const next = await iterator.next();
      if (next.value) {
        mostRecentTimestamp = Math.max(mostRecentTimestamp, next.value.timestamp + next.value.duration);
      }
      return next;
    },
    isDestroyed: () => {
      return destroyed;
    },
    addQueuedAudioNode: ({
      node,
      timestamp,
      buffer,
      scheduledTime,
      playbackRate,
      scheduledAtAnchor
    }) => {
      queuedAudioNodes.push({
        node,
        timestamp,
        buffer,
        scheduledTime,
        playbackRate,
        scheduledAtAnchor
      });
    },
    removeQueuedAudioNode: (node) => {
      const index = queuedAudioNodes.findIndex((n) => n.node === node);
      if (index !== -1) {
        queuedAudioNodes.splice(index, 1);
      }
    },
    getAndClearAudioChunksForAfterResuming: () => {
      const chunks = audioChunksForAfterResuming.slice();
      audioChunksForAfterResuming.length = 0;
      return chunks;
    },
    getQueuedPeriod: () => {
      let until = -Infinity;
      let from = Infinity;
      for (const node of queuedAudioNodes) {
        until = Math.max(until, node.timestamp + node.buffer.duration);
        from = Math.min(from, node.timestamp);
      }
      for (const chunk of audioChunksForAfterResuming) {
        until = Math.max(until, chunk.timestamp + chunk.buffer.duration);
        from = Math.min(from, chunk.timestamp);
      }
      if (!Number.isFinite(from) || !Number.isFinite(until)) {
        return null;
      }
      return {
        from,
        until
      };
    },
    tryToSatisfySeek,
    bufferAsFarAsPossible,
    addChunkForAfterResuming,
    moveQueuedChunksToPauseQueue,
    getNumberOfChunksAfterResuming
  };
};
var isAlreadyQueued = (time, queuedPeriod) => {
  if (!queuedPeriod) {
    return false;
  }
  return time >= queuedPeriod.from && time < queuedPeriod.until;
};

// src/make-iterator-with-priming.ts
var AUDIO_PRIMING_SECONDS = 0.5;
var PREDECODE_AHEAD_SECONDS = 8;
function makePredecodingIterator(inner) {
  const buffer = [];
  let consumerEndTime = 0;
  let innerDone = false;
  let returned = false;
  let fetching = false;
  let waiter = null;
  const prefetch = () => {
    if (fetching || returned || innerDone) {
      return;
    }
    const lastBuffered = buffer.length > 0 ? buffer[buffer.length - 1] : null;
    const bufferedEndTime = lastBuffered ? lastBuffered.timestamp + lastBuffered.duration : consumerEndTime;
    if (bufferedEndTime >= consumerEndTime + PREDECODE_AHEAD_SECONDS) {
      return;
    }
    fetching = true;
    inner.next().then((result) => {
      fetching = false;
      if (returned) {
        return;
      }
      if (result.done) {
        innerDone = true;
        if (waiter) {
          const w = waiter;
          waiter = null;
          w({ value: undefined, done: true });
        }
        return;
      }
      if (waiter) {
        const w = waiter;
        waiter = null;
        const buf = result.value;
        consumerEndTime = buf.timestamp + buf.duration;
        w({ value: buf, done: false });
        prefetch();
        return;
      }
      buffer.push(result.value);
      prefetch();
    }, () => {
      fetching = false;
      innerDone = true;
      if (waiter) {
        const w = waiter;
        waiter = null;
        w({ value: undefined, done: true });
      }
    });
  };
  prefetch();
  const _return = () => {
    returned = true;
    buffer.length = 0;
    if (waiter) {
      const w = waiter;
      waiter = null;
      w({ value: undefined, done: true });
    }
    inner.return(undefined);
    return Promise.resolve({ value: undefined, done: true });
  };
  const iterator = {
    next() {
      if (buffer.length > 0) {
        const buf = buffer.shift();
        consumerEndTime = buf.timestamp + buf.duration;
        prefetch();
        return Promise.resolve({ value: buf, done: false });
      }
      if (innerDone) {
        return Promise.resolve({
          value: undefined,
          done: true
        });
      }
      return new Promise((resolve) => {
        waiter = resolve;
        prefetch();
      });
    },
    return: _return,
    throw(e) {
      returned = true;
      buffer.length = 0;
      return inner.throw(e);
    },
    [Symbol.asyncIterator]() {
      return iterator;
    }
  };
  return iterator;
}
async function* makeIteratorWithPrimingInner(audioSink, timeToSeek, maximumTimestamp) {
  const primingStart = Math.max(0, timeToSeek - AUDIO_PRIMING_SECONDS);
  const iterator = audioSink.buffers(primingStart, maximumTimestamp);
  for await (const buffer of iterator) {
    if (buffer.timestamp + buffer.duration <= timeToSeek) {
      continue;
    }
    yield buffer;
  }
}
var makeIteratorWithPriming = ({
  audioSink,
  timeToSeek,
  maximumTimestamp
}) => {
  return makePredecodingIterator(makeIteratorWithPrimingInner(audioSink, timeToSeek, maximumTimestamp));
};

// src/prewarm-iterator-for-looping.ts
var makePrewarmedVideoIteratorCache = (videoSink) => {
  const prewarmedVideoIterators = new Map;
  const prewarmIteratorForLooping = ({ timeToSeek }) => {
    if (!prewarmedVideoIterators.has(timeToSeek)) {
      prewarmedVideoIterators.set(timeToSeek, videoSink.canvases(timeToSeek));
    }
  };
  const makeIteratorOrUsePrewarmed = (timeToSeek) => {
    const prewarmedIterator = prewarmedVideoIterators.get(timeToSeek);
    if (prewarmedIterator) {
      prewarmedVideoIterators.delete(timeToSeek);
      return prewarmedIterator;
    }
    const iterator = videoSink.canvases(timeToSeek);
    return iterator;
  };
  const destroy = () => {
    for (const iterator of prewarmedVideoIterators.values()) {
      iterator.return();
    }
    prewarmedVideoIterators.clear();
  };
  return {
    prewarmIteratorForLooping,
    makeIteratorOrUsePrewarmed,
    destroy
  };
};
var makeKey = (timeToSeek, maximumTimestamp) => {
  return `${timeToSeek}-${maximumTimestamp}`;
};
var makePrewarmedAudioIteratorCache = (audioSink) => {
  const prewarmedAudioIterators = new Map;
  const prewarmIteratorForLooping = ({
    timeToSeek,
    maximumTimestamp
  }) => {
    if (!prewarmedAudioIterators.has(makeKey(timeToSeek, maximumTimestamp))) {
      prewarmedAudioIterators.set(makeKey(timeToSeek, maximumTimestamp), makeIteratorWithPriming({ audioSink, timeToSeek, maximumTimestamp }));
    }
  };
  const makeIteratorOrUsePrewarmed = (timeToSeek, maximumTimestamp) => {
    const prewarmedIterator = prewarmedAudioIterators.get(makeKey(timeToSeek, maximumTimestamp));
    if (prewarmedIterator) {
      prewarmedAudioIterators.delete(makeKey(timeToSeek, maximumTimestamp));
      return prewarmedIterator;
    }
    const iterator = makeIteratorWithPriming({
      audioSink,
      timeToSeek,
      maximumTimestamp
    });
    return iterator;
  };
  const destroy = () => {
    for (const iterator of prewarmedAudioIterators.values()) {
      iterator.return();
    }
    prewarmedAudioIterators.clear();
  };
  return {
    prewarmIteratorForLooping,
    makeIteratorOrUsePrewarmed,
    destroy
  };
};

// src/audio-iterator-manager.ts
var MAX_BUFFER_AHEAD_SECONDS = 8;
var audioIteratorManager = ({
  audioTrack,
  delayPlaybackHandleIfNotPremounting,
  sharedAudioContext,
  getIsLooping,
  getEndTime,
  getStartTime,
  initialMuted,
  drawDebugOverlay
}) => {
  let muted = initialMuted;
  let currentVolume = 1;
  const gainNode = sharedAudioContext.audioContext.createGain();
  gainNode.connect(sharedAudioContext.audioContext.destination);
  const audioSink = new AudioBufferSink(audioTrack);
  const prewarmedAudioIteratorCache = makePrewarmedAudioIteratorCache(audioSink);
  let audioBufferIterator = null;
  let audioIteratorsCreated = 0;
  let currentDelayHandle = null;
  const scheduleAudioChunk = ({
    buffer,
    mediaTimestamp,
    playbackRate,
    scheduleAudioNode,
    debugAudioScheduling
  }) => {
    if (!audioBufferIterator) {
      throw new Error("Audio buffer iterator not found");
    }
    if (sharedAudioContext.audioContext.state !== "running") {
      throw new Error("Tried to schedule node while audio context is not running");
    }
    if (muted) {
      return;
    }
    const node = sharedAudioContext.audioContext.createBufferSource();
    node.buffer = buffer;
    node.playbackRate.value = playbackRate;
    node.connect(gainNode);
    const started = scheduleAudioNode(node, mediaTimestamp);
    if (started.type === "not-started") {
      if (debugAudioScheduling) {
        Internals4.Log.info({ logLevel: "trace", tag: "audio-scheduling" }, "not started, disconnected: %s %s", mediaTimestamp.toFixed(3), buffer.duration.toFixed(3));
      }
      node.disconnect();
      return;
    }
    const iterator = audioBufferIterator;
    iterator.addQueuedAudioNode({
      node,
      timestamp: mediaTimestamp,
      buffer,
      scheduledTime: started.scheduledTime,
      playbackRate,
      scheduledAtAnchor: sharedAudioContext.audioSyncAnchor.value
    });
    node.onended = () => {
      setTimeout(() => {
        iterator.removeQueuedAudioNode(node);
      }, 30);
    };
  };
  const resumeScheduledAudioChunks = ({
    playbackRate,
    scheduleAudioNode,
    debugAudioScheduling
  }) => {
    if (muted) {
      return;
    }
    if (!audioBufferIterator) {
      return;
    }
    for (const chunk of audioBufferIterator.getAndClearAudioChunksForAfterResuming()) {
      scheduleAudioChunk({
        buffer: chunk.buffer,
        mediaTimestamp: chunk.timestamp,
        playbackRate,
        scheduleAudioNode,
        debugAudioScheduling
      });
    }
  };
  const onAudioChunk = ({
    getIsPlaying,
    buffer,
    playbackRate,
    scheduleAudioNode,
    debugAudioScheduling
  }) => {
    if (muted) {
      return;
    }
    const startTime = getStartTime();
    const endTime = getEndTime();
    if (buffer.timestamp + buffer.duration <= startTime) {
      return;
    }
    if (buffer.timestamp >= endTime) {
      return;
    }
    if (getIsPlaying() && sharedAudioContext.audioContext.state === "running" && (sharedAudioContext.audioContext.getOutputTimestamp().contextTime ?? 0) > 0) {
      resumeScheduledAudioChunks({
        playbackRate,
        scheduleAudioNode,
        debugAudioScheduling
      });
      scheduleAudioChunk({
        buffer: buffer.buffer,
        mediaTimestamp: buffer.timestamp,
        playbackRate,
        scheduleAudioNode,
        debugAudioScheduling
      });
    } else {
      if (!audioBufferIterator) {
        throw new Error("Audio buffer iterator not found");
      }
      if (debugAudioScheduling) {
        Internals4.Log.info({ logLevel: "trace", tag: "audio-scheduling" }, "not ready, added to queue: %s %s", buffer.timestamp.toFixed(3), buffer.duration.toFixed(3));
      }
      audioBufferIterator.addChunkForAfterResuming(buffer.buffer, buffer.timestamp);
    }
    drawDebugOverlay();
  };
  const startAudioIterator = async ({
    nonce,
    playbackRate,
    startFromSecond,
    getIsPlaying,
    scheduleAudioNode,
    debugAudioScheduling
  }) => {
    let __stack = [];
    try {
      if (muted) {
        return;
      }
      audioBufferIterator?.destroy(sharedAudioContext);
      const delayHandle = __using(__stack, delayPlaybackHandleIfNotPremounting(), 0);
      currentDelayHandle = delayHandle;
      const iterator = makeAudioIterator({
        startFromSecond,
        maximumTimestamp: getEndTime(),
        cache: prewarmedAudioIteratorCache,
        debugAudioScheduling
      });
      audioIteratorsCreated++;
      audioBufferIterator = iterator;
      try {
        for (let i = 0;i < 6; i++) {
          const result = await iterator.getNext();
          if (iterator.isDestroyed()) {
            return;
          }
          if (nonce.isStale()) {
            return;
          }
          if (!result.value) {
            return;
          }
          onAudioChunk({
            getIsPlaying,
            buffer: result.value,
            playbackRate,
            scheduleAudioNode,
            debugAudioScheduling
          });
        }
        await iterator.bufferAsFarAsPossible((buffer) => {
          if (!nonce.isStale()) {
            onAudioChunk({
              getIsPlaying,
              buffer,
              playbackRate,
              scheduleAudioNode,
              debugAudioScheduling
            });
          }
        }, Math.min(startFromSecond + MAX_BUFFER_AHEAD_SECONDS, getEndTime()));
      } catch (e) {
        if (e instanceof InputDisposedError) {
          return;
        }
        throw e;
      }
    } catch (_catch) {
      var _err = _catch, _hasErr = 1;
    } finally {
      __callDispose(__stack, _err, _hasErr);
    }
  };
  const pausePlayback = () => {
    if (!audioBufferIterator) {
      return;
    }
    audioBufferIterator.moveQueuedChunksToPauseQueue();
  };
  const seek = async ({
    newTime,
    nonce,
    playbackRate,
    getIsPlaying,
    scheduleAudioNode,
    debugAudioScheduling
  }) => {
    if (muted) {
      return;
    }
    if (getIsLooping()) {
      if (getEndTime() - newTime < 1) {
        prewarmedAudioIteratorCache.prewarmIteratorForLooping({
          timeToSeek: getStartTime(),
          maximumTimestamp: getEndTime()
        });
      }
    }
    if (!audioBufferIterator) {
      await startAudioIterator({
        nonce,
        playbackRate,
        startFromSecond: newTime,
        getIsPlaying,
        scheduleAudioNode,
        debugAudioScheduling
      });
      return;
    }
    const queuedPeriod = audioBufferIterator.getQueuedPeriod();
    const queuedPeriodMinusLatency = queuedPeriod ? {
      from: queuedPeriod.from - ALLOWED_GLOBAL_TIME_ANCHOR_SHIFT - sharedAudioContext.audioContext.baseLatency - sharedAudioContext.audioContext.outputLatency,
      until: queuedPeriod.until
    } : null;
    const currentTimeIsAlreadyQueued = isAlreadyQueued(newTime, queuedPeriodMinusLatency);
    if (!currentTimeIsAlreadyQueued) {
      const audioSatisfyResult = await audioBufferIterator.tryToSatisfySeek(newTime, (buffer) => {
        if (!nonce.isStale()) {
          onAudioChunk({
            getIsPlaying,
            buffer,
            playbackRate,
            scheduleAudioNode,
            debugAudioScheduling
          });
        }
      });
      if (nonce.isStale()) {
        return;
      }
      if (audioSatisfyResult.type === "ended") {
        return;
      }
      if (audioSatisfyResult.type === "not-satisfied") {
        await startAudioIterator({
          nonce,
          playbackRate,
          startFromSecond: newTime,
          getIsPlaying,
          scheduleAudioNode,
          debugAudioScheduling
        });
        return;
      }
      if (audioSatisfyResult.type === "satisfied") {}
    }
    await audioBufferIterator.bufferAsFarAsPossible((buffer) => {
      if (!nonce.isStale()) {
        onAudioChunk({
          getIsPlaying,
          buffer,
          playbackRate,
          scheduleAudioNode,
          debugAudioScheduling
        });
      }
    }, Math.min(newTime + MAX_BUFFER_AHEAD_SECONDS, getEndTime()));
  };
  return {
    startAudioIterator,
    resumeScheduledAudioChunks,
    pausePlayback,
    getAudioBufferIterator: () => audioBufferIterator,
    destroyIterator: () => {
      prewarmedAudioIteratorCache.destroy();
      audioBufferIterator?.destroy(sharedAudioContext);
      audioBufferIterator = null;
      if (currentDelayHandle) {
        currentDelayHandle.unblock();
        currentDelayHandle = null;
      }
    },
    seek,
    getAudioIteratorsCreated: () => audioIteratorsCreated,
    setMuted: (newMuted) => {
      muted = newMuted;
      gainNode.gain.value = muted ? 0 : currentVolume;
    },
    setVolume: (volume) => {
      currentVolume = Math.max(0, volume);
      gainNode.gain.value = muted ? 0 : currentVolume;
    },
    scheduleAudioChunk
  };
};

// src/debug-overlay/preview-overlay.ts
var drawPreviewOverlay = ({
  context,
  audioTime,
  audioContextState,
  audioSyncAnchor,
  playing,
  audioIteratorManager: audioIteratorManager2,
  videoIteratorManager,
  playbackRate
}) => {
  const anchorValue = audioSyncAnchor?.value ?? 0;
  const lines = [
    "Debug overlay",
    `Video iterators created: ${videoIteratorManager?.getVideoIteratorsCreated()}`,
    `Audio iterators created: ${audioIteratorManager2?.getAudioIteratorsCreated()}`,
    `Frames rendered: ${videoIteratorManager?.getFramesRendered()}`,
    `Audio context state: ${audioContextState}`,
    audioTime ? `Audio time: ${((audioTime - anchorValue) * playbackRate).toFixed(3)}s` : null
  ].filter(Boolean);
  if (audioIteratorManager2) {
    const queuedPeriod = audioIteratorManager2.getAudioBufferIterator()?.getQueuedPeriod();
    if (queuedPeriod) {
      const aheadText = audioTime ? ` (${(queuedPeriod.until - (audioTime - anchorValue) * playbackRate).toFixed(3)}s ahead)` : "";
      lines.push(`Audio queued until ${queuedPeriod.until.toFixed(3)}s${aheadText}`);
    }
    lines.push(`Playing: ${playing}`);
  }
  const lineHeight = 30;
  const boxPaddingX = 10;
  const boxPaddingY = 10;
  const boxLeft = 20;
  const boxTop = 20;
  const boxWidth = 600;
  const boxHeight = lines.length * lineHeight + 2 * boxPaddingY;
  context.fillStyle = "rgba(0, 0, 0, 1)";
  context.fillRect(boxLeft, boxTop, boxWidth, boxHeight);
  context.fillStyle = "white";
  context.font = "24px sans-serif";
  context.textBaseline = "top";
  for (let i = 0;i < lines.length; i++) {
    context.fillText(lines[i], boxLeft + boxPaddingX, boxTop + boxPaddingY + i * lineHeight);
  }
};

// src/is-type-of-error.ts
function isNetworkError(error) {
  if (error.message.includes("Failed to fetch") || error.message.includes("Load failed") || error.message.includes("NetworkError when attempting to fetch resource")) {
    return true;
  }
  return false;
}
function isUnsupportedConfigurationError(error) {
  return error.message.includes("Unsupported configuration");
}

// src/nonce-manager.ts
var makeNonceManager = () => {
  let nonce = 0;
  const createAsyncOperation = () => {
    nonce++;
    const currentNonce = nonce;
    return {
      isStale: () => nonce !== currentNonce
    };
  };
  return {
    createAsyncOperation
  };
};

// src/video-iterator-manager.ts
import { CanvasSink } from "mediabunny";
import { Internals as Internals5 } from "remotion";

// src/video/video-preview-iterator.ts
var createVideoIterator = async (timeToSeek, cache) => {
  let destroyed = false;
  const iterator = cache.makeIteratorOrUsePrewarmed(timeToSeek);
  let iteratorEnded = false;
  const initialFrame = (await iterator.next())?.value ?? null;
  let lastReturnedFrame = initialFrame;
  const getNextOrNullIfNotAvailable = async () => {
    const next = iterator.next();
    const result = await Promise.race([
      next,
      new Promise((resolve) => {
        Promise.resolve().then(() => resolve());
      })
    ]);
    if (!result) {
      return {
        type: "need-to-wait-for-it",
        waitPromise: async () => {
          const res = await next;
          if (res.value) {
            lastReturnedFrame = res.value;
          } else {
            iteratorEnded = true;
          }
          return res.value;
        }
      };
    }
    if (result.value) {
      lastReturnedFrame = result.value;
    } else {
      iteratorEnded = true;
    }
    return {
      type: "got-frame-or-end",
      frame: result.value ?? null
    };
  };
  const destroy = () => {
    destroyed = true;
    lastReturnedFrame = null;
    iterator.return().catch(() => {
      return;
    });
  };
  const tryToSatisfySeek = async (time) => {
    if (lastReturnedFrame) {
      const frameTimestamp = roundTo4Digits(lastReturnedFrame.timestamp);
      if (roundTo4Digits(time) < frameTimestamp) {
        const lastFrameWasInitialFrame = lastReturnedFrame === initialFrame;
        const firstFrameDoesSatisfy = lastFrameWasInitialFrame && roundTo4Digits(time) >= roundTo4Digits(timeToSeek);
        if (firstFrameDoesSatisfy) {
          return {
            type: "satisfied",
            frame: lastReturnedFrame
          };
        }
        return {
          type: "not-satisfied",
          reason: `iterator is too far, most recently returned ${frameTimestamp}`
        };
      }
      const frameEndTimestamp = roundTo4Digits(lastReturnedFrame.timestamp + lastReturnedFrame.duration);
      const timestamp = roundTo4Digits(time);
      if (frameTimestamp <= timestamp && frameEndTimestamp > timestamp) {
        return {
          type: "satisfied",
          frame: lastReturnedFrame
        };
      }
    }
    if (iteratorEnded) {
      if (lastReturnedFrame) {
        return {
          type: "satisfied",
          frame: lastReturnedFrame
        };
      }
      return {
        type: "not-satisfied",
        reason: "iterator ended"
      };
    }
    while (true) {
      const frame = await getNextOrNullIfNotAvailable();
      if (frame.type === "need-to-wait-for-it") {
        return {
          type: "not-satisfied",
          reason: "iterator did not have frame ready"
        };
      }
      if (frame.type === "got-frame-or-end") {
        if (frame.frame === null) {
          iteratorEnded = true;
          if (lastReturnedFrame) {
            return {
              type: "satisfied",
              frame: lastReturnedFrame
            };
          }
          return {
            type: "not-satisfied",
            reason: "iterator ended and did not have frame ready"
          };
        }
        const frameTimestamp = roundTo4Digits(frame.frame.timestamp);
        const frameEndTimestamp = roundTo4Digits(frame.frame.timestamp + frame.frame.duration);
        const timestamp = roundTo4Digits(time);
        if (frameTimestamp <= timestamp && frameEndTimestamp > timestamp) {
          return {
            type: "satisfied",
            frame: frame.frame
          };
        }
        continue;
      }
      throw new Error("Unreachable");
    }
  };
  return {
    destroy,
    initialFrame,
    isDestroyed: () => {
      return destroyed;
    },
    tryToSatisfySeek
  };
};

// src/video-iterator-manager.ts
var videoIteratorManager = ({
  delayPlaybackHandleIfNotPremounting,
  canvas,
  context,
  drawDebugOverlay,
  logLevel,
  getOnVideoFrameCallback,
  videoTrack,
  getEndTime,
  getStartTime,
  getIsLooping
}) => {
  let videoIteratorsCreated = 0;
  let videoFrameIterator = null;
  let framesRendered = 0;
  let currentDelayHandle = null;
  if (canvas) {
    canvas.width = videoTrack.displayWidth;
    canvas.height = videoTrack.displayHeight;
  }
  const canvasSink = new CanvasSink(videoTrack, {
    poolSize: 2,
    fit: "contain",
    alpha: true
  });
  const prewarmedVideoIteratorCache = makePrewarmedVideoIteratorCache(canvasSink);
  const drawFrame = (frame) => {
    if (context && canvas) {
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(frame.canvas, 0, 0);
    }
    framesRendered++;
    drawDebugOverlay();
    const callback = getOnVideoFrameCallback();
    if (callback) {
      callback(frame.canvas);
    }
    Internals5.Log.trace({ logLevel, tag: "@remotion/media" }, `[MediaPlayer] Drew frame ${frame.timestamp.toFixed(3)}s`);
  };
  const startVideoIterator = async (timeToSeek, nonce) => {
    let __stack = [];
    try {
      videoFrameIterator?.destroy();
      const delayHandle = __using(__stack, delayPlaybackHandleIfNotPremounting(), 0);
      currentDelayHandle = delayHandle;
      const iterator = await createVideoIterator(timeToSeek, prewarmedVideoIteratorCache);
      videoIteratorsCreated++;
      videoFrameIterator = iterator;
      if (iterator.isDestroyed()) {
        return;
      }
      if (nonce.isStale()) {
        return;
      }
      if (videoFrameIterator.isDestroyed()) {
        return;
      }
      if (!iterator.initialFrame) {
        return;
      }
      drawFrame(iterator.initialFrame);
    } catch (_catch) {
      var _err = _catch, _hasErr = 1;
    } finally {
      __callDispose(__stack, _err, _hasErr);
    }
  };
  const seek = async ({ newTime, nonce }) => {
    if (!videoFrameIterator) {
      return;
    }
    if (getIsLooping()) {
      if (getEndTime() - newTime < 1) {
        prewarmedVideoIteratorCache.prewarmIteratorForLooping({
          timeToSeek: getStartTime()
        });
      }
    }
    const videoSatisfyResult = await videoFrameIterator.tryToSatisfySeek(newTime);
    if (videoSatisfyResult.type === "satisfied") {
      drawFrame(videoSatisfyResult.frame);
      return;
    }
    if (nonce.isStale()) {
      return;
    }
    await startVideoIterator(newTime, nonce);
  };
  return {
    startVideoIterator,
    getVideoIteratorsCreated: () => videoIteratorsCreated,
    seek,
    destroy: () => {
      prewarmedVideoIteratorCache.destroy();
      videoFrameIterator?.destroy();
      if (context && canvas) {
        context.clearRect(0, 0, canvas.width, canvas.height);
      }
      if (currentDelayHandle) {
        currentDelayHandle.unblock();
        currentDelayHandle = null;
      }
      videoFrameIterator = null;
    },
    getVideoFrameIterator: () => videoFrameIterator,
    drawFrame,
    getFramesRendered: () => framesRendered
  };
};

// src/media-player.ts
class MediaPlayer {
  canvas;
  context;
  src;
  logLevel;
  playbackRate;
  globalPlaybackRate;
  audioStreamIndex;
  sharedAudioContext;
  audioIteratorManager = null;
  videoIteratorManager = null;
  sequenceOffset;
  playing = false;
  loop = false;
  fps;
  trimBefore;
  trimAfter;
  durationInFrames;
  totalDuration;
  debugOverlay = false;
  debugAudioScheduling = false;
  nonceManager;
  onVideoFrameCallback = null;
  initializationPromise = null;
  bufferState;
  isPremounting;
  isPostmounting;
  seekPromiseChain = Promise.resolve();
  constructor({
    canvas,
    src,
    logLevel,
    sharedAudioContext,
    loop,
    trimBefore,
    trimAfter,
    playbackRate,
    globalPlaybackRate,
    audioStreamIndex,
    fps,
    debugOverlay,
    debugAudioScheduling,
    bufferState,
    isPremounting,
    isPostmounting,
    durationInFrames,
    onVideoFrameCallback,
    playing,
    sequenceOffset
  }) {
    this.canvas = canvas ?? null;
    this.src = src;
    this.logLevel = logLevel;
    this.sharedAudioContext = sharedAudioContext;
    this.playbackRate = playbackRate;
    this.globalPlaybackRate = globalPlaybackRate;
    this.loop = loop;
    this.trimBefore = trimBefore;
    this.trimAfter = trimAfter;
    this.audioStreamIndex = audioStreamIndex ?? 0;
    this.fps = fps;
    this.debugOverlay = debugOverlay;
    this.debugAudioScheduling = debugAudioScheduling;
    this.bufferState = bufferState;
    this.isPremounting = isPremounting;
    this.isPostmounting = isPostmounting;
    this.durationInFrames = durationInFrames;
    this.nonceManager = makeNonceManager();
    this.onVideoFrameCallback = onVideoFrameCallback;
    this.playing = playing;
    this.sequenceOffset = sequenceOffset;
    this.input = new Input({
      source: new UrlSource(this.src),
      formats: ALL_FORMATS
    });
    if (canvas) {
      const context = canvas.getContext("2d", {
        alpha: true,
        desynchronized: true
      });
      if (!context) {
        throw new Error("Could not get 2D context from canvas");
      }
      this.context = context;
    } else {
      this.context = null;
    }
  }
  input;
  isDisposalError() {
    return this.input.disposed === true;
  }
  initialize(startTimeUnresolved, initialMuted) {
    const promise = this._initialize(startTimeUnresolved, initialMuted);
    this.initializationPromise = promise;
    this.seekPromiseChain = promise;
    return promise;
  }
  getStartTime() {
    return (this.trimBefore ?? 0) / this.fps;
  }
  getEndTime() {
    const mediaEndTime = calculateEndTime({
      mediaDurationInSeconds: this.totalDuration,
      ifNoMediaDuration: "fail",
      src: this.src,
      trimAfter: this.trimAfter,
      trimBefore: this.trimBefore,
      fps: this.fps
    });
    if (this.loop) {
      return mediaEndTime;
    }
    const sequenceEndMediaTime = this.durationInFrames / this.fps * this.playbackRate + (this.trimBefore ?? 0) / this.fps;
    return Math.min(mediaEndTime, sequenceEndMediaTime);
  }
  async _initialize(startTimeUnresolved, initialMuted) {
    let __stack = [];
    try {
      const _ = __using(__stack, this.delayPlaybackHandleIfNotPremounting(), 0);
      try {
        if (this.input.disposed) {
          return { type: "disposed" };
        }
        try {
          await this.input.getFormat();
        } catch (error) {
          if (this.isDisposalError()) {
            return { type: "disposed" };
          }
          const err = error;
          if (isNetworkError(err)) {
            throw error;
          }
          Internals6.Log.error({ logLevel: this.logLevel, tag: "@remotion/media" }, `[MediaPlayer] Failed to recognize format for ${this.src}`, error);
          return { type: "unknown-container-format" };
        }
        const [durationInSeconds, videoTrack, audioTracks] = await Promise.all([
          this.input.computeDuration(),
          this.input.getPrimaryVideoTrack(),
          this.input.getAudioTracks()
        ]);
        if (this.input.disposed) {
          return { type: "disposed" };
        }
        this.totalDuration = durationInSeconds;
        const audioTrack = audioTracks[this.audioStreamIndex] ?? null;
        if (!videoTrack && !audioTrack) {
          return { type: "no-tracks" };
        }
        if (videoTrack) {
          const canDecode = await videoTrack.canDecode();
          if (!canDecode) {
            return { type: "cannot-decode" };
          }
          if (this.input.disposed) {
            return { type: "disposed" };
          }
          this.videoIteratorManager = videoIteratorManager({
            videoTrack,
            delayPlaybackHandleIfNotPremounting: this.delayPlaybackHandleIfNotPremounting,
            context: this.context,
            canvas: this.canvas,
            getOnVideoFrameCallback: () => this.onVideoFrameCallback,
            logLevel: this.logLevel,
            drawDebugOverlay: this.drawDebugOverlay,
            getEndTime: () => this.getEndTime(),
            getStartTime: () => this.getStartTime(),
            getIsLooping: () => this.loop
          });
        }
        const startTime = this.getTrimmedTime(startTimeUnresolved);
        if (startTime === null) {
          throw new Error(`should have asserted that the time is not null`);
        }
        if (audioTrack && this.sharedAudioContext) {
          const canDecode = await audioTrack.canDecode();
          if (!canDecode) {
            return { type: "cannot-decode" };
          }
          if (this.input.disposed) {
            return { type: "disposed" };
          }
          this.audioIteratorManager = audioIteratorManager({
            audioTrack,
            delayPlaybackHandleIfNotPremounting: this.delayPlaybackHandleIfNotPremounting,
            sharedAudioContext: this.sharedAudioContext,
            getIsLooping: () => this.loop,
            getEndTime: () => this.getEndTime(),
            getStartTime: () => this.getStartTime(),
            initialMuted,
            drawDebugOverlay: this.drawDebugOverlay
          });
        }
        const nonce = this.nonceManager.createAsyncOperation();
        try {
          await Promise.all([
            this.audioIteratorManager ? this.audioIteratorManager.startAudioIterator({
              nonce,
              playbackRate: this.playbackRate * this.globalPlaybackRate,
              startFromSecond: startTime,
              getIsPlaying: () => this.playing,
              scheduleAudioNode: this.scheduleAudioNode,
              debugAudioScheduling: this.debugAudioScheduling
            }) : Promise.resolve(),
            this.videoIteratorManager ? this.videoIteratorManager.startVideoIterator(startTime, nonce) : Promise.resolve()
          ]);
        } catch (error) {
          if (this.isDisposalError()) {
            return { type: "disposed" };
          }
          Internals6.Log.error({ logLevel: this.logLevel, tag: "@remotion/media" }, "[MediaPlayer] Failed to start audio and video iterators", error);
        }
        return { type: "success", durationInSeconds };
      } catch (error) {
        const err = error;
        if (isNetworkError(err)) {
          Internals6.Log.error({ logLevel: this.logLevel, tag: "@remotion/media" }, `[MediaPlayer] Network/CORS error for ${this.src}`, err);
          return { type: "network-error" };
        }
        Internals6.Log.error({ logLevel: this.logLevel, tag: "@remotion/media" }, "[MediaPlayer] Failed to initialize", error);
        throw error;
      }
    } catch (_catch) {
      var _err = _catch, _hasErr = 1;
    } finally {
      __callDispose(__stack, _err, _hasErr);
    }
  }
  seekToWithQueue = async (newTime) => {
    const nonce = this.nonceManager.createAsyncOperation();
    await this.seekPromiseChain;
    this.seekPromiseChain = this.seekToDoNotCallDirectly(newTime, nonce);
    await this.seekPromiseChain;
  };
  async seekTo(time) {
    const newTime = this.getTrimmedTime(time);
    if (newTime === null) {
      throw new Error(`should have asserted that the time is not null`);
    }
    await this.seekToWithQueue(newTime);
  }
  async seekToDoNotCallDirectly(newTime, nonce) {
    if (nonce.isStale()) {
      return;
    }
    const shouldSeekAudio = this.audioIteratorManager && this.getAudioPlaybackTime(this.sharedAudioContext?.audioContext.currentTime ?? 0) !== newTime;
    try {
      await Promise.all([
        this.videoIteratorManager?.seek({
          newTime,
          nonce
        }),
        shouldSeekAudio ? this.audioIteratorManager?.seek({
          newTime,
          nonce,
          playbackRate: this.playbackRate * this.globalPlaybackRate,
          getIsPlaying: () => this.playing,
          scheduleAudioNode: this.scheduleAudioNode,
          debugAudioScheduling: this.debugAudioScheduling
        }) : null
      ]);
    } catch (error) {
      if (this.isDisposalError()) {
        return;
      }
      throw error;
    }
  }
  playAudio() {
    if (this.audioIteratorManager && this.sharedAudioContext?.audioContext.state === "running" && (this.sharedAudioContext?.audioContext?.getOutputTimestamp().contextTime ?? 0) > 0) {
      this.audioIteratorManager.resumeScheduledAudioChunks({
        playbackRate: this.playbackRate * this.globalPlaybackRate,
        scheduleAudioNode: this.scheduleAudioNode,
        debugAudioScheduling: this.debugAudioScheduling
      });
    }
  }
  play() {
    this.playAudio();
    if (this.playing) {
      return;
    }
    this.playing = true;
    this.drawDebugOverlay();
  }
  delayPlaybackHandleIfNotPremounting = () => {
    if (this.isPremounting || this.isPostmounting) {
      return {
        unblock: () => {},
        [Symbol.dispose]: () => {}
      };
    }
    const { unblock } = this.bufferState.delayPlayback();
    return {
      unblock,
      [Symbol.dispose]: () => {
        unblock();
      }
    };
  };
  pause() {
    if (!this.playing) {
      return;
    }
    this.playing = false;
    this.audioIteratorManager?.pausePlayback();
    this.drawDebugOverlay();
  }
  setMuted(muted) {
    this.audioIteratorManager?.setMuted(muted);
  }
  setVolume(volume) {
    if (!this.audioIteratorManager) {
      return;
    }
    this.audioIteratorManager.setVolume(volume);
  }
  getTrimmedTime(unloopedTimeInSeconds) {
    return getTimeInSeconds({
      unloopedTimeInSeconds,
      playbackRate: this.playbackRate,
      loop: this.loop,
      trimBefore: this.trimBefore,
      trimAfter: this.trimAfter,
      mediaDurationInSeconds: this.totalDuration ?? null,
      fps: this.fps,
      ifNoMediaDuration: "infinity",
      src: this.src
    });
  }
  async updateAfterTrimChange(unloopedTimeInSeconds) {
    if (!this.audioIteratorManager && !this.videoIteratorManager) {
      return;
    }
    const newMediaTime = this.getTrimmedTime(unloopedTimeInSeconds);
    this.audioIteratorManager?.destroyIterator();
    if (newMediaTime !== null) {
      if (!this.playing && this.videoIteratorManager) {
        await this.seekToWithQueue(newMediaTime);
      }
    }
  }
  async setTrimBefore(trimBefore, unloopedTimeInSeconds) {
    if (this.trimBefore !== trimBefore) {
      this.trimBefore = trimBefore;
      await this.updateAfterTrimChange(unloopedTimeInSeconds);
    }
  }
  async setTrimAfter(trimAfter, unloopedTimeInSeconds) {
    if (this.trimAfter !== trimAfter) {
      this.trimAfter = trimAfter;
      await this.updateAfterTrimChange(unloopedTimeInSeconds);
    }
  }
  setDebugOverlay(debugOverlay) {
    this.debugOverlay = debugOverlay;
  }
  setDebugAudioScheduling(debugAudioScheduling) {
    this.debugAudioScheduling = debugAudioScheduling;
  }
  rescheduleAudioChunks() {
    if (!this.audioIteratorManager) {
      return;
    }
    if (!this.sharedAudioContext) {
      return;
    }
    const iterator = this.audioIteratorManager.getAudioBufferIterator();
    if (!iterator) {
      return;
    }
    iterator.moveQueuedChunksToPauseQueue();
    if (this.playing && this.sharedAudioContext.audioContext.state === "running" && (this.sharedAudioContext.audioContext?.getOutputTimestamp().contextTime ?? 0) > 0) {
      this.audioIteratorManager.resumeScheduledAudioChunks({
        playbackRate: this.playbackRate * this.globalPlaybackRate,
        scheduleAudioNode: this.scheduleAudioNode,
        debugAudioScheduling: this.debugAudioScheduling
      });
    }
  }
  async setPlaybackRate(rate, unloopedTimeInSeconds) {
    const previousRate = this.playbackRate;
    if (previousRate !== rate) {
      this.playbackRate = rate;
      this.rescheduleAudioChunks();
      await this.seekTo(unloopedTimeInSeconds);
    }
  }
  setGlobalPlaybackRate(rate) {
    const previousRate = this.globalPlaybackRate;
    if (previousRate !== rate) {
      this.globalPlaybackRate = rate;
      this.rescheduleAudioChunks();
    }
  }
  setFps(fps) {
    this.fps = fps;
  }
  setIsPremounting(isPremounting) {
    this.isPremounting = isPremounting;
  }
  setIsPostmounting(isPostmounting) {
    this.isPostmounting = isPostmounting;
  }
  setLoop(loop) {
    this.loop = loop;
  }
  setSequenceOffset(offset) {
    this.sequenceOffset = offset;
  }
  setDurationInFrames(durationInFrames) {
    this.durationInFrames = durationInFrames;
  }
  async dispose() {
    if (this.initializationPromise) {
      try {
        await this.initializationPromise;
      } catch {}
    }
    this.nonceManager.createAsyncOperation();
    this.videoIteratorManager?.destroy();
    this.audioIteratorManager?.destroyIterator();
    this.input.dispose();
  }
  scheduleAudioNode = (node, mediaTimestamp) => {
    if (!this.sharedAudioContext) {
      throw new Error("Shared audio context not found");
    }
    const { audioContext } = this.sharedAudioContext;
    const { currentTime } = audioContext;
    const globalTime = (currentTime - this.sharedAudioContext.audioSyncAnchor.value) * this.globalPlaybackRate;
    const timeInSeconds = globalTime - this.sequenceOffset;
    const localTime = this.getTrimmedTime(timeInSeconds);
    if (localTime === null) {
      throw new Error("hmm, should not render!");
    }
    const targetTime = (mediaTimestamp - localTime) / (this.playbackRate * this.globalPlaybackRate);
    return this.sharedAudioContext.scheduleAudioNode({
      node,
      mediaTimestamp,
      targetTime,
      currentTime,
      sequenceEndTime: this.getEndTime(),
      sequenceStartTime: this.getStartTime(),
      debugAudioScheduling: this.debugAudioScheduling
    });
  };
  getAudioPlaybackTime(currentTime) {
    if (!this.sharedAudioContext) {
      throw new Error("Shared audio context not found");
    }
    const globalTime = (currentTime - this.sharedAudioContext.audioSyncAnchor.value) * this.globalPlaybackRate;
    const localTime = globalTime - this.sequenceOffset;
    const trimmedTime = this.getTrimmedTime(localTime);
    if (trimmedTime !== null) {
      return trimmedTime;
    }
    return localTime * this.playbackRate + (this.trimBefore ?? 0) / this.fps;
  }
  setVideoFrameCallback(callback) {
    this.onVideoFrameCallback = callback;
  }
  drawDebugOverlay = () => {
    if (!this.debugOverlay)
      return;
    if (this.context && this.canvas) {
      drawPreviewOverlay({
        context: this.context,
        audioTime: this.sharedAudioContext?.audioContext.currentTime ?? null,
        audioContextState: this.sharedAudioContext?.audioContext.state ?? null,
        audioSyncAnchor: this.sharedAudioContext?.audioSyncAnchor ?? null,
        audioIteratorManager: this.audioIteratorManager,
        playing: this.playing,
        videoIteratorManager: this.videoIteratorManager,
        playbackRate: this.playbackRate * this.globalPlaybackRate
      });
    }
  };
}

// src/on-error.ts
var callOnErrorAndResolve = ({
  onError,
  error,
  disallowFallback,
  isClientSideRendering,
  clientSideError
}) => {
  const result = onError?.(error);
  if (isClientSideRendering) {
    return ["fail", clientSideError];
  }
  if (result) {
    return [result, error];
  }
  if (disallowFallback) {
    return ["fail", error];
  }
  return ["fallback", error];
};

// src/show-in-timeline.ts
import { useMemo } from "react";
import { Internals as Internals7, useVideoConfig } from "remotion";
var useLoopDisplay = ({
  loop,
  mediaDurationInSeconds,
  playbackRate,
  trimAfter,
  trimBefore
}) => {
  const { durationInFrames: compDuration, fps } = useVideoConfig();
  const loopDisplay = useMemo(() => {
    if (!loop || !mediaDurationInSeconds) {
      return;
    }
    const durationInFrames = Internals7.calculateMediaDuration({
      mediaDurationInFrames: mediaDurationInSeconds * fps,
      playbackRate,
      trimAfter,
      trimBefore
    });
    const maxTimes = compDuration / durationInFrames;
    return {
      numberOfTimes: maxTimes,
      startOffset: 0,
      durationInFrames
    };
  }, [
    compDuration,
    fps,
    loop,
    mediaDurationInSeconds,
    playbackRate,
    trimAfter,
    trimBefore
  ]);
  return loopDisplay;
};

// src/use-common-effects.ts
import { useContext, useLayoutEffect } from "react";
import { Internals as Internals8 } from "remotion";
var useCommonEffects = ({
  mediaPlayerRef,
  mediaPlayerReady,
  currentTimeRef,
  playing,
  isPlayerBuffering,
  frame,
  trimBefore,
  trimAfter,
  effectiveMuted,
  userPreferredVolume,
  playbackRate,
  globalPlaybackRate,
  fps,
  sequenceOffset,
  loop,
  debugAudioScheduling,
  durationInFrames,
  isPremounting,
  isPostmounting,
  currentTime,
  logLevel,
  sharedAudioContext,
  label
}) => {
  const absoluteTime = Internals8.useAbsoluteTimelinePosition();
  const { playing: playingWhilePremounting } = useContext(Internals8.PremountContext);
  useLayoutEffect(() => {
    if (sharedAudioContext?.audioContext && sharedAudioContext.audioSyncAnchor) {
      setGlobalTimeAnchor({
        audioContext: sharedAudioContext.audioContext,
        audioSyncAnchor: sharedAudioContext.audioSyncAnchor,
        absoluteTimeInSeconds: absoluteTime / fps,
        globalPlaybackRate,
        debugAudioScheduling,
        logLevel
      });
    }
  }, [
    absoluteTime,
    globalPlaybackRate,
    sharedAudioContext,
    fps,
    debugAudioScheduling,
    logLevel
  ]);
  if (playingWhilePremounting) {
    mediaPlayerRef.current?.playAudio();
  }
  useLayoutEffect(() => {
    const mediaPlayer = mediaPlayerRef.current;
    if (!mediaPlayer)
      return;
    if (playing && !isPlayerBuffering) {
      mediaPlayer.play();
    } else {
      mediaPlayer.pause();
    }
  }, [
    isPlayerBuffering,
    playing,
    logLevel,
    mediaPlayerReady,
    frame,
    mediaPlayerRef
  ]);
  useLayoutEffect(() => {
    const mediaPlayer = mediaPlayerRef.current;
    if (!mediaPlayer || !mediaPlayerReady) {
      return;
    }
    mediaPlayer.setTrimBefore(trimBefore, currentTimeRef.current);
  }, [trimBefore, mediaPlayerReady, mediaPlayerRef, currentTimeRef]);
  useLayoutEffect(() => {
    const mediaPlayer = mediaPlayerRef.current;
    if (!mediaPlayer || !mediaPlayerReady) {
      return;
    }
    mediaPlayer.setTrimAfter(trimAfter, currentTimeRef.current);
  }, [trimAfter, mediaPlayerReady, mediaPlayerRef, currentTimeRef]);
  useLayoutEffect(() => {
    const mediaPlayer = mediaPlayerRef.current;
    if (!mediaPlayer || !mediaPlayerReady)
      return;
    mediaPlayer.setMuted(effectiveMuted);
  }, [effectiveMuted, mediaPlayerReady, mediaPlayerRef]);
  useLayoutEffect(() => {
    const mediaPlayer = mediaPlayerRef.current;
    if (!mediaPlayer || !mediaPlayerReady) {
      return;
    }
    mediaPlayer.setVolume(userPreferredVolume);
  }, [userPreferredVolume, mediaPlayerReady, mediaPlayerRef]);
  useLayoutEffect(() => {
    const mediaPlayer = mediaPlayerRef.current;
    if (!mediaPlayer || !mediaPlayerReady) {
      return;
    }
    mediaPlayer.setPlaybackRate(playbackRate, currentTimeRef.current);
  }, [playbackRate, mediaPlayerReady, mediaPlayerRef, currentTimeRef]);
  useLayoutEffect(() => {
    const mediaPlayer = mediaPlayerRef.current;
    if (!mediaPlayer || !mediaPlayerReady) {
      return;
    }
    mediaPlayer.setGlobalPlaybackRate(globalPlaybackRate);
  }, [globalPlaybackRate, mediaPlayerReady, mediaPlayerRef]);
  useLayoutEffect(() => {
    const mediaPlayer = mediaPlayerRef.current;
    if (!mediaPlayer || !mediaPlayerReady) {
      return;
    }
    mediaPlayer.setLoop(loop);
  }, [loop, mediaPlayerReady, mediaPlayerRef]);
  useLayoutEffect(() => {
    const mediaPlayer = mediaPlayerRef.current;
    if (!mediaPlayer || !mediaPlayerReady) {
      return;
    }
    mediaPlayer.setDurationInFrames(durationInFrames);
  }, [durationInFrames, mediaPlayerReady, mediaPlayerRef]);
  useLayoutEffect(() => {
    const mediaPlayer = mediaPlayerRef.current;
    if (!mediaPlayer || !mediaPlayerReady) {
      return;
    }
    mediaPlayer.setIsPremounting(isPremounting);
  }, [isPremounting, mediaPlayerReady, mediaPlayerRef]);
  useLayoutEffect(() => {
    const mediaPlayer = mediaPlayerRef.current;
    if (!mediaPlayer || !mediaPlayerReady) {
      return;
    }
    mediaPlayer.setIsPostmounting(isPostmounting);
  }, [isPostmounting, mediaPlayerReady, mediaPlayerRef]);
  useLayoutEffect(() => {
    const mediaPlayer = mediaPlayerRef.current;
    if (!mediaPlayer || !mediaPlayerReady) {
      return;
    }
    mediaPlayer.setFps(fps);
  }, [fps, mediaPlayerReady, mediaPlayerRef]);
  useLayoutEffect(() => {
    const mediaPlayer = mediaPlayerRef.current;
    if (!mediaPlayer || !mediaPlayerReady) {
      return;
    }
    mediaPlayer.setSequenceOffset(sequenceOffset);
  }, [sequenceOffset, mediaPlayerReady, mediaPlayerRef]);
  useLayoutEffect(() => {
    const mediaPlayer = mediaPlayerRef.current;
    if (!mediaPlayer || !mediaPlayerReady) {
      return;
    }
    mediaPlayer.setDebugAudioScheduling(debugAudioScheduling);
  }, [debugAudioScheduling, mediaPlayerReady, mediaPlayerRef]);
  useLayoutEffect(() => {
    const mediaPlayer = mediaPlayerRef.current;
    if (!mediaPlayer || !mediaPlayerReady)
      return;
    mediaPlayer.seekTo(currentTime).catch(() => {});
    Internals8.Log.trace({ logLevel, tag: "@remotion/media" }, `[${label}] Updating target time to ${currentTime.toFixed(3)}s`);
  }, [currentTime, logLevel, mediaPlayerReady, label, mediaPlayerRef]);
};

// src/use-media-in-timeline.ts
import { useContext as useContext2, useState, useEffect } from "react";
import { Internals as Internals9, useCurrentFrame } from "remotion";
var useMediaInTimeline = ({
  volume,
  mediaVolume,
  src,
  mediaType,
  playbackRate,
  displayName,
  stack,
  showInTimeline,
  premountDisplay,
  postmountDisplay,
  loopDisplay,
  trimBefore,
  trimAfter,
  controls
}) => {
  const parentSequence = useContext2(Internals9.SequenceContext);
  const startsAt = Internals9.useMediaStartsAt();
  const { registerSequence, unregisterSequence } = useContext2(Internals9.SequenceManager);
  const [sequenceId] = useState(() => String(Math.random()));
  const [mediaId] = useState(() => String(Math.random()));
  const frame = useCurrentFrame();
  const {
    volumes,
    duration,
    doesVolumeChange,
    nonce,
    rootId,
    isStudio,
    finalDisplayName
  } = Internals9.useBasicMediaInTimeline({
    volume,
    mediaVolume,
    mediaType,
    src,
    displayName,
    trimBefore,
    trimAfter,
    playbackRate
  });
  useEffect(() => {
    if (!src) {
      throw new Error("No src passed");
    }
    if (!isStudio && window.process?.env?.NODE_ENV !== "test") {
      return;
    }
    if (!showInTimeline) {
      return;
    }
    const loopIteration = loopDisplay ? Math.floor(frame / loopDisplay.durationInFrames) : 0;
    if (loopDisplay) {
      registerSequence({
        type: "sequence",
        premountDisplay,
        postmountDisplay,
        parent: parentSequence?.id ?? null,
        displayName: finalDisplayName,
        rootId,
        showInTimeline: true,
        nonce: nonce.get(),
        loopDisplay,
        stack,
        from: 0,
        duration,
        id: sequenceId,
        controls: null
      });
    }
    registerSequence({
      type: mediaType,
      src,
      id: mediaId,
      duration: loopDisplay?.durationInFrames ?? duration,
      from: loopDisplay ? loopIteration * loopDisplay.durationInFrames : 0,
      parent: loopDisplay ? sequenceId : parentSequence?.id ?? null,
      displayName: finalDisplayName,
      rootId,
      volume: volumes,
      showInTimeline: true,
      nonce: nonce.get(),
      startMediaFrom: 0 - startsAt + (trimBefore ?? 0),
      doesVolumeChange,
      loopDisplay: undefined,
      playbackRate,
      stack,
      premountDisplay: null,
      postmountDisplay: null,
      controls: controls ?? null
    });
    return () => {
      if (loopDisplay) {
        unregisterSequence(sequenceId);
      }
      unregisterSequence(mediaId);
    };
  }, [
    controls,
    doesVolumeChange,
    duration,
    finalDisplayName,
    isStudio,
    loopDisplay,
    mediaId,
    mediaType,
    nonce,
    parentSequence?.id,
    playbackRate,
    postmountDisplay,
    premountDisplay,
    registerSequence,
    rootId,
    sequenceId,
    showInTimeline,
    src,
    stack,
    startsAt,
    unregisterSequence,
    volumes,
    frame,
    trimBefore
  ]);
  return {
    id: mediaId
  };
};

// src/audio/audio-for-preview.tsx
import { jsx } from "react/jsx-runtime";
var {
  useUnsafeVideoConfig,
  Timeline,
  SharedAudioContext,
  useMediaMutedState,
  useMediaVolumeState,
  useFrameForVolumeProp,
  evaluateVolume,
  warnAboutTooHighVolume,
  usePreload,
  SequenceContext
} = Internals10;
var AudioForPreviewAssertedShowing = ({
  src,
  playbackRate,
  logLevel,
  muted,
  volume,
  loopVolumeCurveBehavior,
  loop,
  trimAfter,
  trimBefore,
  name,
  showInTimeline,
  stack,
  disallowFallbackToHtml5Audio,
  toneFrequency,
  audioStreamIndex,
  fallbackHtml5AudioProps,
  debugAudioScheduling,
  onError,
  controls
}) => {
  const videoConfig = useUnsafeVideoConfig();
  const frame = useCurrentFrame2();
  const mediaPlayerRef = useRef(null);
  const initialTrimBeforeRef = useRef(trimBefore);
  const initialTrimAfterRef = useRef(trimAfter);
  const [mediaPlayerReady, setMediaPlayerReady] = useState2(false);
  const [shouldFallbackToNativeAudio, setShouldFallbackToNativeAudio] = useState2(false);
  const [playing] = Timeline.usePlayingState();
  const timelineContext = Internals10.useTimelineContext();
  const globalPlaybackRate = timelineContext.playbackRate;
  const sharedAudioContext = useContext3(SharedAudioContext);
  const buffer = useBufferState();
  const [mediaMuted] = useMediaMutedState();
  const [mediaVolume] = useMediaVolumeState();
  const [mediaDurationInSeconds, setMediaDurationInSeconds] = useState2(null);
  const volumePropFrame = useFrameForVolumeProp(loopVolumeCurveBehavior ?? "repeat");
  const userPreferredVolume = evaluateVolume({
    frame: volumePropFrame,
    volume,
    mediaVolume
  });
  warnAboutTooHighVolume(userPreferredVolume);
  if (!videoConfig) {
    throw new Error("No video config found");
  }
  if (!src) {
    throw new TypeError("No `src` was passed to <NewAudioForPreview>.");
  }
  const currentTime = frame / videoConfig.fps;
  const currentTimeRef = useRef(currentTime);
  currentTimeRef.current = currentTime;
  const preloadedSrc = usePreload(src);
  const parentSequence = useContext3(SequenceContext);
  const isPremounting = Boolean(parentSequence?.premounting);
  const isPostmounting = Boolean(parentSequence?.postmounting);
  const sequenceOffset = ((parentSequence?.cumulatedFrom ?? 0) + (parentSequence?.relativeFrom ?? 0)) / videoConfig.fps;
  const loopDisplay = useLoopDisplay({
    loop,
    mediaDurationInSeconds,
    playbackRate,
    trimAfter,
    trimBefore
  });
  useMediaInTimeline({
    volume,
    mediaVolume,
    mediaType: "audio",
    src,
    playbackRate,
    displayName: name ?? null,
    stack,
    showInTimeline,
    premountDisplay: parentSequence?.premountDisplay ?? null,
    postmountDisplay: parentSequence?.postmountDisplay ?? null,
    loopDisplay,
    trimAfter,
    trimBefore,
    controls
  });
  const bufferingContext = useContext3(Internals10.BufferingContextReact);
  if (!bufferingContext) {
    throw new Error("useMediaPlayback must be used inside a <BufferingContext>");
  }
  const effectiveMuted = muted || mediaMuted || userPreferredVolume <= 0;
  const isPlayerBuffering = Internals10.useIsPlayerBuffering(bufferingContext);
  const initialPlaying = useRef(playing && !isPlayerBuffering);
  const initialIsPremounting = useRef(isPremounting);
  const initialIsPostmounting = useRef(isPostmounting);
  const initialGlobalPlaybackRate = useRef(globalPlaybackRate);
  const initialPlaybackRate = useRef(playbackRate);
  const initialMuted = useRef(effectiveMuted);
  const initialSequenceOffset = useRef(sequenceOffset);
  useCommonEffects({
    mediaPlayerRef,
    mediaPlayerReady,
    currentTimeRef,
    playing,
    isPlayerBuffering,
    frame,
    trimBefore,
    trimAfter,
    effectiveMuted,
    userPreferredVolume,
    playbackRate,
    globalPlaybackRate,
    fps: videoConfig.fps,
    sequenceOffset,
    loop,
    debugAudioScheduling,
    durationInFrames: videoConfig.durationInFrames,
    isPremounting,
    isPostmounting,
    currentTime,
    logLevel,
    sharedAudioContext,
    label: "AudioForPreview"
  });
  useEffect2(() => {
    if (!sharedAudioContext)
      return;
    if (!sharedAudioContext.audioContext)
      return;
    const { audioContext, audioSyncAnchor, scheduleAudioNode } = sharedAudioContext;
    try {
      const player = new MediaPlayer({
        src: preloadedSrc,
        logLevel,
        sharedAudioContext: { audioContext, audioSyncAnchor, scheduleAudioNode },
        loop,
        trimAfter: initialTrimAfterRef.current,
        trimBefore: initialTrimBeforeRef.current,
        fps: videoConfig.fps,
        canvas: null,
        playbackRate: initialPlaybackRate.current,
        audioStreamIndex: audioStreamIndex ?? 0,
        debugOverlay: false,
        debugAudioScheduling,
        bufferState: buffer,
        isPostmounting: initialIsPostmounting.current,
        isPremounting: initialIsPremounting.current,
        globalPlaybackRate: initialGlobalPlaybackRate.current,
        durationInFrames: videoConfig.durationInFrames,
        onVideoFrameCallback: null,
        playing: initialPlaying.current,
        sequenceOffset: initialSequenceOffset.current
      });
      mediaPlayerRef.current = player;
      player.initialize(currentTimeRef.current, initialMuted.current).then((result) => {
        if (result.type === "disposed") {
          return;
        }
        const handleError = (error, fallbackMessage) => {
          const [action, errorToUse] = callOnErrorAndResolve({
            onError,
            error,
            disallowFallback: disallowFallbackToHtml5Audio,
            isClientSideRendering: false,
            clientSideError: error
          });
          if (action === "fail") {
            throw errorToUse;
          } else {
            Internals10.Log.warn({ logLevel, tag: "@remotion/media" }, fallbackMessage);
            setShouldFallbackToNativeAudio(true);
          }
        };
        if (result.type === "unknown-container-format") {
          handleError(new Error(`Unknown container format ${preloadedSrc}.`), `Unknown container format for ${preloadedSrc} (Supported formats: https://www.remotion.dev/docs/mediabunny/formats), falling back to <Html5Audio>`);
          return;
        }
        if (result.type === "network-error") {
          handleError(new Error(`Network error fetching ${preloadedSrc}.`), `Network error fetching ${preloadedSrc}, falling back to <Html5Audio>`);
          return;
        }
        if (result.type === "cannot-decode") {
          handleError(new Error(`Cannot decode ${preloadedSrc}.`), `Cannot decode ${preloadedSrc}, falling back to <Html5Audio>`);
          return;
        }
        if (result.type === "no-tracks") {
          handleError(new Error(`No video or audio tracks found for ${preloadedSrc}.`), `No video or audio tracks found for ${preloadedSrc}, falling back to <Html5Audio>`);
          return;
        }
        if (result.type === "success") {
          setMediaPlayerReady(true);
          setMediaDurationInSeconds(result.durationInSeconds);
          Internals10.Log.trace({ logLevel, tag: "@remotion/media" }, `[AudioForPreview] MediaPlayer initialized successfully`);
        }
      }).catch((error) => {
        const [action, errorToUse] = callOnErrorAndResolve({
          onError,
          error,
          disallowFallback: disallowFallbackToHtml5Audio,
          isClientSideRendering: false,
          clientSideError: error
        });
        if (action === "fail") {
          throw errorToUse;
        } else {
          Internals10.Log.error({ logLevel, tag: "@remotion/media" }, "[AudioForPreview] Failed to initialize MediaPlayer", error);
          setShouldFallbackToNativeAudio(true);
        }
      });
    } catch (error) {
      const [action, errorToUse] = callOnErrorAndResolve({
        error,
        onError,
        disallowFallback: disallowFallbackToHtml5Audio,
        isClientSideRendering: false,
        clientSideError: error
      });
      if (action === "fail") {
        throw errorToUse;
      }
      Internals10.Log.error({ logLevel, tag: "@remotion/media" }, "[AudioForPreview] MediaPlayer initialization failed", errorToUse);
      setShouldFallbackToNativeAudio(true);
    }
    return () => {
      if (mediaPlayerRef.current) {
        Internals10.Log.trace({ logLevel, tag: "@remotion/media" }, `[AudioForPreview] Disposing MediaPlayer`);
        mediaPlayerRef.current.dispose();
        mediaPlayerRef.current = null;
      }
      setMediaPlayerReady(false);
      setShouldFallbackToNativeAudio(false);
    };
  }, [
    preloadedSrc,
    logLevel,
    sharedAudioContext,
    loop,
    videoConfig.fps,
    audioStreamIndex,
    disallowFallbackToHtml5Audio,
    debugAudioScheduling,
    buffer,
    onError,
    videoConfig.durationInFrames
  ]);
  if (shouldFallbackToNativeAudio && !disallowFallbackToHtml5Audio) {
    return /* @__PURE__ */ jsx(RemotionAudio, {
      src,
      muted,
      volume,
      startFrom: trimBefore,
      endAt: trimAfter,
      playbackRate,
      loopVolumeCurveBehavior,
      name,
      loop,
      showInTimeline,
      stack: stack ?? undefined,
      toneFrequency,
      audioStreamIndex,
      pauseWhenBuffering: fallbackHtml5AudioProps?.pauseWhenBuffering,
      crossOrigin: fallbackHtml5AudioProps?.crossOrigin,
      ...fallbackHtml5AudioProps
    });
  }
  return null;
};
var AudioForPreview = ({
  loop = false,
  src,
  logLevel,
  muted,
  name,
  volume,
  loopVolumeCurveBehavior,
  playbackRate = 1,
  trimAfter,
  trimBefore,
  showInTimeline,
  stack,
  disallowFallbackToHtml5Audio,
  toneFrequency,
  audioStreamIndex,
  fallbackHtml5AudioProps,
  debugAudioScheduling,
  onError,
  controls
}) => {
  const preloadedSrc = usePreload(src);
  const defaultLogLevel = Internals10.useLogLevel();
  const frame = useCurrentFrame2();
  const videoConfig = useVideoConfig2();
  const currentTime = frame / videoConfig.fps;
  const showShow = useMemo2(() => {
    return getTimeInSeconds({
      unloopedTimeInSeconds: currentTime,
      playbackRate,
      loop,
      trimBefore,
      trimAfter,
      mediaDurationInSeconds: Infinity,
      fps: videoConfig.fps,
      ifNoMediaDuration: "infinity",
      src
    }) !== null;
  }, [
    currentTime,
    playbackRate,
    src,
    trimAfter,
    trimBefore,
    videoConfig.fps,
    loop
  ]);
  if (!showShow) {
    return null;
  }
  return /* @__PURE__ */ jsx(AudioForPreviewAssertedShowing, {
    audioStreamIndex: audioStreamIndex ?? 0,
    src: preloadedSrc,
    playbackRate,
    logLevel: logLevel ?? defaultLogLevel,
    muted: muted ?? false,
    volume: volume ?? 1,
    loopVolumeCurveBehavior: loopVolumeCurveBehavior ?? "repeat",
    loop,
    trimAfter,
    trimBefore,
    name,
    showInTimeline: showInTimeline ?? true,
    stack,
    disallowFallbackToHtml5Audio: disallowFallbackToHtml5Audio ?? false,
    toneFrequency,
    debugAudioScheduling: debugAudioScheduling ?? false,
    onError,
    fallbackHtml5AudioProps,
    controls
  });
};

// src/audio/audio-for-rendering.tsx
import { useContext as useContext4, useLayoutEffect as useLayoutEffect2, useMemo as useMemo3, useState as useState3 } from "react";
import {
  cancelRender as cancelRender2,
  Html5Audio,
  Internals as Internals18,
  random,
  useCurrentFrame as useCurrentFrame3,
  useDelayRender,
  useRemotionEnvironment
} from "remotion";

// src/caches.ts
import React2 from "react";
import { cancelRender, Internals as Internals15 } from "remotion";

// src/audio-extraction/audio-manager.ts
import { Internals as Internals12 } from "remotion";

// src/audio-extraction/audio-iterator.ts
import { Internals as Internals11 } from "remotion";

// src/audio-extraction/audio-cache.ts
var makeAudioCache = () => {
  const timestamps = [];
  const samples = {};
  const addFrame = (sample) => {
    timestamps.push(sample.timestamp);
    samples[sample.timestamp] = sample;
  };
  const clearBeforeThreshold = (threshold) => {
    for (const timestamp of timestamps.slice()) {
      const endTimestamp = timestamp + samples[timestamp].duration;
      if (endTimestamp < threshold) {
        const isLast = timestamp === timestamps[timestamps.length - 1];
        if (isLast) {
          continue;
        }
        samples[timestamp].close();
        delete samples[timestamp];
        timestamps.splice(timestamps.indexOf(timestamp), 1);
      }
    }
  };
  const deleteAll = () => {
    for (const timestamp of timestamps) {
      samples[timestamp].close();
      delete samples[timestamp];
    }
    timestamps.length = 0;
  };
  const getSamples = (timestamp, durationInSeconds) => {
    const selected = [];
    for (let i = 0;i < timestamps.length; i++) {
      const sampleTimestamp = timestamps[i];
      const sample = samples[sampleTimestamp];
      if (sample.timestamp + sample.duration - 0.0000000001 <= timestamp) {
        continue;
      }
      if (sample.timestamp >= timestamp + durationInSeconds - 0.0000000001) {
        break;
      }
      selected.push(sample);
    }
    return selected;
  };
  const getOpenTimestamps = () => {
    return timestamps;
  };
  const getOldestTimestamp = () => {
    return timestamps[0];
  };
  const getNewestTimestamp = () => {
    if (timestamps.length === 0) {
      return null;
    }
    const sample = samples[timestamps[timestamps.length - 1]];
    return sample.timestamp + sample.duration;
  };
  return {
    addFrame,
    clearBeforeThreshold,
    deleteAll,
    getSamples,
    getOldestTimestamp,
    getNewestTimestamp,
    getOpenTimestamps
  };
};

// src/audio-extraction/audio-iterator.ts
var EXTRA_THRESHOLD_IN_SECONDS = 1.5;
var safetyOutOfOrderThreshold = 0.2;
var warned = {};
var warnAboutMatroskaOnce = (src, logLevel) => {
  if (warned[src]) {
    return;
  }
  warned[src] = true;
  Internals11.Log.warn({ logLevel, tag: "@remotion/media" }, `Audio from ${src} will need to be read from the beginning. https://www.remotion.dev/docs/media/support#matroska-limitation`);
};
var makeAudioIterator2 = ({
  audioSampleSink,
  isMatroska,
  startTimestamp,
  src,
  actualMatroskaTimestamps,
  logLevel
}) => {
  const sampleIterator = audioSampleSink.samples(isMatroska ? 0 : Math.max(0, startTimestamp - EXTRA_THRESHOLD_IN_SECONDS));
  if (isMatroska) {
    warnAboutMatroskaOnce(src, logLevel);
  }
  let fullDuration = null;
  const cache = makeAudioCache();
  let lastUsed = Date.now();
  const getNextSample = async () => {
    lastUsed = Date.now();
    const { value: sample, done } = await sampleIterator.next();
    if (done) {
      fullDuration = cache.getNewestTimestamp();
      return null;
    }
    const realTimestamp = actualMatroskaTimestamps.getRealTimestamp(sample.timestamp);
    if (realTimestamp !== null && realTimestamp !== sample.timestamp) {
      sample.setTimestamp(realTimestamp);
    }
    actualMatroskaTimestamps.observeTimestamp(sample.timestamp);
    actualMatroskaTimestamps.observeTimestamp(sample.timestamp + sample.duration);
    cache.addFrame(sample);
    return sample;
  };
  const getSamples = async (timestamp, durationInSeconds) => {
    lastUsed = Date.now();
    if (fullDuration !== null && timestamp > fullDuration) {
      cache.clearBeforeThreshold(fullDuration - safetyOutOfOrderThreshold);
      return [];
    }
    const samples = cache.getSamples(timestamp, durationInSeconds);
    const newestTimestamp = cache.getNewestTimestamp();
    if (newestTimestamp !== null) {
      if (newestTimestamp >= timestamp + durationInSeconds - 0.0000000001) {
        return samples;
      }
    }
    while (true) {
      const sample = await getNextSample();
      const deleteBefore = fullDuration === null ? timestamp : Math.min(timestamp, fullDuration);
      cache.clearBeforeThreshold(deleteBefore - safetyOutOfOrderThreshold);
      if (sample === null) {
        break;
      }
      if (sample.timestamp + sample.duration - 0.0000000001 <= timestamp) {
        continue;
      }
      if (sample.timestamp >= timestamp + durationInSeconds - 0.0000000001) {
        break;
      }
      samples.push(sample);
    }
    return samples;
  };
  const logOpenFrames = () => {
    const openTimestamps = cache.getOpenTimestamps();
    if (openTimestamps.length > 0) {
      const first = openTimestamps[0];
      const last = openTimestamps[openTimestamps.length - 1];
      Internals11.Log.verbose({ logLevel, tag: "@remotion/media" }, "Open audio samples for src", src, `${first.toFixed(3)}...${last.toFixed(3)}`);
    }
  };
  const getCacheStats = () => {
    return {
      count: cache.getOpenTimestamps().length,
      size: cache.getOpenTimestamps().reduce((acc, t) => acc + t, 0)
    };
  };
  const canSatisfyRequestedTime = (timestamp) => {
    const oldestTimestamp = cache.getOldestTimestamp() ?? startTimestamp;
    if (fullDuration !== null && timestamp > fullDuration) {
      return true;
    }
    return oldestTimestamp < timestamp && Math.abs(oldestTimestamp - timestamp) < 10;
  };
  const prepareForDeletion = () => {
    cache.deleteAll();
    sampleIterator.return().then((value) => {
      if (value.value) {
        value.value.close();
      }
    });
    fullDuration = null;
  };
  let op = Promise.resolve([]);
  return {
    src,
    getSamples: (ts, dur) => {
      op = op.then(() => getSamples(ts, dur));
      return op;
    },
    waitForCompletion: async () => {
      await op;
      return true;
    },
    canSatisfyRequestedTime,
    logOpenFrames,
    getCacheStats,
    getLastUsed: () => lastUsed,
    prepareForDeletion,
    startTimestamp,
    clearBeforeThreshold: cache.clearBeforeThreshold,
    getOldestTimestamp: cache.getOldestTimestamp,
    getNewestTimestamp: cache.getNewestTimestamp
  };
};

// src/audio-extraction/audio-manager.ts
var makeAudioManager = () => {
  const iterators = [];
  const makeIterator = ({
    timeInSeconds,
    src,
    audioSampleSink,
    isMatroska,
    actualMatroskaTimestamps,
    logLevel
  }) => {
    const iterator = makeAudioIterator2({
      audioSampleSink,
      isMatroska,
      startTimestamp: timeInSeconds,
      src,
      actualMatroskaTimestamps,
      logLevel
    });
    iterators.push(iterator);
    return iterator;
  };
  const getIteratorMostInThePast = () => {
    let mostInThePast = null;
    let mostInThePastIterator = null;
    for (const iterator of iterators) {
      const lastUsed = iterator.getLastUsed();
      if (mostInThePast === null || lastUsed < mostInThePast) {
        mostInThePast = lastUsed;
        mostInThePastIterator = iterator;
      }
    }
    return mostInThePastIterator;
  };
  const deleteOldestIterator = () => {
    const iterator = getIteratorMostInThePast();
    if (iterator) {
      iterator.prepareForDeletion();
      iterators.splice(iterators.indexOf(iterator), 1);
    }
  };
  const deleteDuplicateIterators = (logLevel) => {
    const seenKeys = new Set;
    for (let i = 0;i < iterators.length; i++) {
      const iterator = iterators[i];
      const key = `${iterator.src}-${iterator.getOldestTimestamp()}-${iterator.getNewestTimestamp()}`;
      if (seenKeys.has(key)) {
        iterator.prepareForDeletion();
        iterators.splice(iterators.indexOf(iterator), 1);
        Internals12.Log.verbose({ logLevel, tag: "@remotion/media" }, `Deleted duplicate iterator for ${iterator.src}`);
      }
      seenKeys.add(key);
    }
  };
  const getIterator = async ({
    src,
    timeInSeconds,
    audioSampleSink,
    isMatroska,
    actualMatroskaTimestamps,
    logLevel,
    maxCacheSize
  }) => {
    let attempts = 0;
    const maxAttempts = 3;
    while ((await getTotalCacheStats()).totalSize > maxCacheSize && attempts < maxAttempts) {
      deleteOldestIterator();
      attempts++;
    }
    if ((await getTotalCacheStats()).totalSize > maxCacheSize && attempts >= maxAttempts) {
      Internals12.Log.warn({ logLevel, tag: "@remotion/media" }, `Audio cache: Exceeded max cache size after ${maxAttempts} attempts. Still ${(await getTotalCacheStats()).totalSize} bytes used, target was ${maxCacheSize} bytes.`);
    }
    for (const iterator of iterators) {
      if (iterator.src === src && await iterator.waitForCompletion() && iterator.canSatisfyRequestedTime(timeInSeconds)) {
        return iterator;
      }
    }
    for (let i = 0;i < iterators.length; i++) {
      const iterator = iterators[i];
      if (iterator.src === src && iterator.startTimestamp === timeInSeconds) {
        iterator.prepareForDeletion();
        iterators.splice(iterators.indexOf(iterator), 1);
      }
    }
    deleteDuplicateIterators(logLevel);
    return makeIterator({
      src,
      timeInSeconds,
      audioSampleSink,
      isMatroska,
      actualMatroskaTimestamps,
      logLevel
    });
  };
  const getCacheStats = () => {
    let totalCount = 0;
    let totalSize = 0;
    for (const iterator of iterators) {
      const { count, size } = iterator.getCacheStats();
      totalCount += count;
      totalSize += size;
    }
    return { count: totalCount, totalSize };
  };
  const logOpenFrames = () => {
    for (const iterator of iterators) {
      iterator.logOpenFrames();
    }
  };
  let queue = Promise.resolve(undefined);
  return {
    getIterator: ({
      src,
      timeInSeconds,
      audioSampleSink,
      isMatroska,
      actualMatroskaTimestamps,
      logLevel,
      maxCacheSize
    }) => {
      queue = queue.then(() => getIterator({
        src,
        timeInSeconds,
        audioSampleSink,
        isMatroska,
        actualMatroskaTimestamps,
        logLevel,
        maxCacheSize
      }));
      return queue;
    },
    getCacheStats,
    getIteratorMostInThePast,
    logOpenFrames,
    deleteDuplicateIterators
  };
};

// src/video-extraction/keyframe-manager.ts
import { Internals as Internals14 } from "remotion";

// src/render-timestamp-range.ts
var renderTimestampRange = (timestamps) => {
  if (timestamps.length === 0) {
    return "(none)";
  }
  if (timestamps.length === 1) {
    return timestamps[0].toFixed(3);
  }
  return `${timestamps[0].toFixed(3)}...${timestamps[timestamps.length - 1].toFixed(3)}`;
};

// src/video-extraction/keyframe-bank.ts
import { Internals as Internals13 } from "remotion";

// src/video-extraction/get-allocation-size.ts
var getAllocationSize = (sample) => {
  if (sample.format === null) {
    return sample.codedHeight * sample.codedWidth * 4;
  }
  return sample.allocationSize();
};

// src/video-extraction/keyframe-bank.ts
var BIGGEST_ALLOWED_JUMP_FORWARD_SECONDS = 3;
var makeKeyframeBank = async ({
  logLevel: parentLogLevel,
  src,
  videoSampleSink,
  initialTimestampRequest
}) => {
  const sampleIterator = videoSampleSink.samples(roundTo4Digits(initialTimestampRequest));
  const frames = {};
  const frameTimestamps = [];
  let hasReachedEndOfVideo = false;
  let lastUsed = Date.now();
  let allocationSize = 0;
  const getDurationOfFrame = (timestamp) => {
    const index = frameTimestamps.indexOf(timestamp);
    if (index === -1) {
      throw new Error(`Frame ${timestamp} not found`);
    }
    const nextTimestamp = frameTimestamps[index + 1];
    if (!nextTimestamp) {
      return null;
    }
    return nextTimestamp - timestamp;
  };
  const deleteFrameAtTimestamp = (timestamp) => {
    allocationSize -= getAllocationSize(frames[timestamp]);
    frameTimestamps.splice(frameTimestamps.indexOf(timestamp), 1);
    frames[timestamp].close();
    delete frames[timestamp];
  };
  const deleteFramesBeforeTimestamp = ({
    logLevel,
    timestampInSeconds
  }) => {
    const deletedTimestamps = [];
    for (const frameTimestamp of frameTimestamps.slice()) {
      if (hasReachedEndOfVideo) {
        const isLast = frameTimestamp === frameTimestamps[frameTimestamps.length - 1];
        if (isLast) {
          continue;
        }
      }
      if (!frames[frameTimestamp]) {
        continue;
      }
      const duration = getDurationOfFrame(frameTimestamp) ?? frames[frameTimestamp].duration;
      if (frameTimestamp + duration < timestampInSeconds) {
        deleteFrameAtTimestamp(frameTimestamp);
        deletedTimestamps.push(frameTimestamp);
      }
    }
    if (deletedTimestamps.length > 0) {
      Internals13.Log.verbose({ logLevel, tag: "@remotion/media" }, `Deleted ${deletedTimestamps.length} frame${deletedTimestamps.length === 1 ? "" : "s"} ${renderTimestampRange(deletedTimestamps)} for src ${src} because it is lower than ${timestampInSeconds}. Remaining: ${renderTimestampRange(frameTimestamps)}`);
    }
  };
  const hasDecodedEnoughForTimestamp = (timestamp) => {
    const lastFrameTimestamp = frameTimestamps[frameTimestamps.length - 1];
    if (!lastFrameTimestamp) {
      return false;
    }
    const lastFrame = frames[lastFrameTimestamp];
    if (!lastFrame) {
      return true;
    }
    const duration = getDurationOfFrame(lastFrameTimestamp) ?? lastFrame.duration;
    return roundTo4Digits(lastFrameTimestamp + duration) > roundTo4Digits(timestamp);
  };
  const addFrame = (frame, logLevel) => {
    if (frames[frame.timestamp]) {
      deleteFrameAtTimestamp(frame.timestamp);
    }
    frames[frame.timestamp] = frame;
    frameTimestamps.push(frame.timestamp);
    allocationSize += getAllocationSize(frame);
    lastUsed = Date.now();
    Internals13.Log.trace({ logLevel, tag: "@remotion/media" }, `Added frame at ${frame.timestamp}sec to bank`);
  };
  const ensureEnoughFramesForTimestamp = async (timestampInSeconds, logLevel, fps) => {
    while (!hasDecodedEnoughForTimestamp(timestampInSeconds)) {
      const sample = await sampleIterator.next();
      if (sample.value) {
        addFrame(sample.value, logLevel);
      }
      if (sample.done) {
        hasReachedEndOfVideo = true;
        break;
      }
      deleteFramesBeforeTimestamp({
        logLevel: parentLogLevel,
        timestampInSeconds: timestampInSeconds - getSafeWindowOfMonotonicity(fps)
      });
    }
    lastUsed = Date.now();
  };
  const getFrameFromTimestamp = async (timestampInSeconds, fps) => {
    lastUsed = Date.now();
    let adjustedTimestamp = timestampInSeconds;
    if (hasReachedEndOfVideo && roundTo4Digits(adjustedTimestamp) > roundTo4Digits(frameTimestamps[frameTimestamps.length - 1])) {
      adjustedTimestamp = frameTimestamps[frameTimestamps.length - 1];
    }
    await ensureEnoughFramesForTimestamp(adjustedTimestamp, parentLogLevel, fps);
    for (let i = frameTimestamps.length - 1;i >= 0; i--) {
      const sample = frames[frameTimestamps[i]];
      if (!sample) {
        return null;
      }
      if (roundTo4Digits(sample.timestamp) <= roundTo4Digits(adjustedTimestamp) || Math.abs(sample.timestamp - adjustedTimestamp) <= 0.001) {
        return sample;
      }
    }
    return frames[frameTimestamps[0]] ?? null;
  };
  const hasTimestampInSecond = async (timestamp, fps) => {
    return await getFrameFromTimestamp(timestamp, fps) !== null;
  };
  const getOpenFrameCount = () => {
    return {
      size: allocationSize,
      timestamps: frameTimestamps
    };
  };
  const getLastUsed = () => {
    return lastUsed;
  };
  let queue = Promise.resolve(undefined);
  const firstFrame = await sampleIterator.next();
  if (!firstFrame.value) {
    throw new Error("No first frame found");
  }
  const startTimestampInSeconds = firstFrame.value.timestamp;
  Internals13.Log.verbose({ logLevel: parentLogLevel, tag: "@remotion/media" }, `Creating keyframe bank from ${startTimestampInSeconds}sec`);
  addFrame(firstFrame.value, parentLogLevel);
  const getRangeOfTimestamps = () => {
    if (frameTimestamps.length === 0) {
      return null;
    }
    const firstTimestamp = frameTimestamps[0];
    const lastTimestamp = frameTimestamps[frameTimestamps.length - 1];
    const lastFrame = frames[lastTimestamp];
    const lastFrameDuration = getDurationOfFrame(lastTimestamp) ?? lastFrame.duration ?? 0;
    return {
      firstTimestamp,
      lastTimestamp: lastTimestamp + lastFrameDuration
    };
  };
  const prepareForDeletion = (logLevel, reason) => {
    const range = getRangeOfTimestamps();
    if (range) {
      Internals13.Log.verbose({ logLevel, tag: "@remotion/media" }, `Preparing for deletion (${reason}) of keyframe bank from ${range?.firstTimestamp}sec to ${range?.lastTimestamp}sec`);
    }
    let framesDeleted = 0;
    for (const frameTimestamp of frameTimestamps.slice()) {
      if (!frames[frameTimestamp]) {
        continue;
      }
      deleteFrameAtTimestamp(frameTimestamp);
      framesDeleted++;
    }
    sampleIterator.return();
    frameTimestamps.length = 0;
    return { framesDeleted };
  };
  const canSatisfyTimestamp = (timestamp) => {
    if (frameTimestamps.length === 0) {
      return false;
    }
    const roundedTimestamp = roundTo4Digits(timestamp);
    const firstFrameTimestamp = roundTo4Digits(frameTimestamps[0]);
    const lastFrameTimestamp = roundTo4Digits(frameTimestamps[frameTimestamps.length - 1]);
    if (hasReachedEndOfVideo && roundedTimestamp > lastFrameTimestamp) {
      return true;
    }
    if (roundedTimestamp < firstFrameTimestamp) {
      const firstFrameIsInitialFrame = firstFrameTimestamp === startTimestampInSeconds;
      const firstFrameDoesSatisfy = firstFrameIsInitialFrame && roundedTimestamp >= initialTimestampRequest;
      return firstFrameDoesSatisfy;
    }
    if (roundedTimestamp - BIGGEST_ALLOWED_JUMP_FORWARD_SECONDS > lastFrameTimestamp) {
      return false;
    }
    return true;
  };
  const keyframeBank = {
    getFrameFromTimestamp: (timestamp, fps) => {
      queue = queue.then(() => getFrameFromTimestamp(timestamp, fps));
      return queue;
    },
    prepareForDeletion,
    hasTimestampInSecond: (timestamp, fps) => {
      queue = queue.then(() => hasTimestampInSecond(timestamp, fps));
      return queue;
    },
    addFrame,
    deleteFramesBeforeTimestamp,
    src,
    getOpenFrameCount,
    getLastUsed,
    canSatisfyTimestamp,
    getRangeOfTimestamps
  };
  return keyframeBank;
};

// src/video-extraction/keyframe-manager.ts
var makeKeyframeManager = () => {
  let sources = {};
  const addKeyframeBank = ({ src, bank }) => {
    sources[src] = sources[src] ?? [];
    sources[src].push(bank);
  };
  const logCacheStats = (logLevel) => {
    let count = 0;
    let totalSize = 0;
    for (const src in sources) {
      for (const bank of sources[src]) {
        const { size, timestamps } = bank.getOpenFrameCount();
        count += timestamps.length;
        totalSize += size;
        if (size === 0) {
          continue;
        }
        Internals14.Log.verbose({ logLevel, tag: "@remotion/media" }, `Open frames for src ${src}: ${renderTimestampRange(timestamps)}`);
      }
    }
    Internals14.Log.verbose({ logLevel, tag: "@remotion/media" }, `Video cache stats: ${count} open frames, ${totalSize} bytes`);
  };
  const getCacheStats = () => {
    let count = 0;
    let totalSize = 0;
    for (const src in sources) {
      for (const bank of sources[src]) {
        const { timestamps, size } = bank.getOpenFrameCount();
        count += timestamps.length;
        totalSize += size;
        if (size === 0) {
          continue;
        }
      }
    }
    return { count, totalSize };
  };
  const getTheKeyframeBankMostInThePast = () => {
    let mostInThePast = null;
    let mostInThePastBank = null;
    let numberOfBanks = 0;
    for (const src in sources) {
      for (const bank of sources[src]) {
        const index = sources[src].indexOf(bank);
        const lastUsed = bank.getLastUsed();
        if (mostInThePast === null || lastUsed < mostInThePast) {
          mostInThePast = lastUsed;
          mostInThePastBank = { src, bank, index };
        }
        numberOfBanks++;
      }
    }
    if (!mostInThePastBank) {
      throw new Error("No keyframe bank found");
    }
    return { mostInThePastBank, numberOfBanks };
  };
  const deleteOldestKeyframeBank = (logLevel) => {
    const {
      mostInThePastBank: {
        bank: mostInThePastBank,
        src: mostInThePastSrc,
        index: mostInThePastIndex
      },
      numberOfBanks
    } = getTheKeyframeBankMostInThePast();
    if (numberOfBanks < 2) {
      return { finish: true };
    }
    if (mostInThePastBank) {
      const range = mostInThePastBank.getRangeOfTimestamps();
      const { framesDeleted } = mostInThePastBank.prepareForDeletion(logLevel, "deleted oldest keyframe bank to stay under max cache size");
      sources[mostInThePastSrc].splice(mostInThePastIndex, 1);
      if (range) {
        Internals14.Log.verbose({ logLevel, tag: "@remotion/media" }, `Deleted ${framesDeleted} frames for src ${mostInThePastSrc} from ${range?.firstTimestamp}sec to ${range?.lastTimestamp}sec to free up memory.`);
      }
    }
    return { finish: false };
  };
  const ensureToStayUnderMaxCacheSize = (logLevel, maxCacheSize) => {
    let cacheStats = getTotalCacheStats();
    let attempts = 0;
    const maxAttempts = 3;
    while (cacheStats.totalSize > maxCacheSize && attempts < maxAttempts) {
      const { finish } = deleteOldestKeyframeBank(logLevel);
      if (finish) {
        break;
      }
      Internals14.Log.verbose({ logLevel, tag: "@remotion/media" }, "Deleted oldest keyframe bank to stay under max cache size", (cacheStats.totalSize / 1024 / 1024).toFixed(1), "out of", (maxCacheSize / 1024 / 1024).toFixed(1));
      cacheStats = getTotalCacheStats();
      attempts++;
    }
    if (cacheStats.totalSize > maxCacheSize && attempts >= maxAttempts) {
      Internals14.Log.warn({ logLevel, tag: "@remotion/media" }, `Exceeded max cache size after ${maxAttempts} attempts. Remaining cache size: ${(cacheStats.totalSize / 1024 / 1024).toFixed(1)} MB, target was ${(maxCacheSize / 1024 / 1024).toFixed(1)} MB.`);
    }
  };
  const clearKeyframeBanksBeforeTime = ({
    timestampInSeconds,
    src,
    logLevel,
    fps
  }) => {
    const threshold = timestampInSeconds - getSafeWindowOfMonotonicity(fps);
    if (!sources[src]) {
      return;
    }
    const banks = sources[src];
    for (const bank of banks) {
      const range = bank.getRangeOfTimestamps();
      if (!range) {
        continue;
      }
      if (range.lastTimestamp < threshold) {
        bank.prepareForDeletion(logLevel, "cleared before threshold " + threshold);
        Internals14.Log.verbose({ logLevel, tag: "@remotion/media" }, `[Video] Cleared frames for src ${src} from ${range.firstTimestamp}sec to ${range.lastTimestamp}sec`);
        const bankIndex = banks.indexOf(bank);
        delete sources[src][bankIndex];
      } else {
        bank.deleteFramesBeforeTimestamp({
          timestampInSeconds: threshold,
          logLevel
        });
      }
    }
    sources[src] = sources[src].filter((bank) => bank !== undefined);
    logCacheStats(logLevel);
  };
  const getKeyframeBankOrRefetch = async ({
    timestamp,
    videoSampleSink,
    src,
    logLevel
  }) => {
    const existingBanks = sources[src] ?? [];
    const existingBank = existingBanks?.find((bank) => bank.canSatisfyTimestamp(timestamp));
    if (!existingBank) {
      Internals14.Log.trace({ logLevel, tag: "@remotion/media" }, `Creating new keyframe bank for src ${src} at timestamp ${timestamp}`);
      const newKeyframeBank = await makeKeyframeBank({
        videoSampleSink,
        logLevel,
        src,
        initialTimestampRequest: timestamp
      });
      addKeyframeBank({ src, bank: newKeyframeBank });
      return newKeyframeBank;
    }
    if (existingBank.canSatisfyTimestamp(timestamp)) {
      Internals14.Log.trace({ logLevel, tag: "@remotion/media" }, `Keyframe bank exists and satisfies timestamp ${timestamp}`);
      return existingBank;
    }
    Internals14.Log.verbose({ logLevel, tag: "@remotion/media" }, `Keyframe bank exists but frame at time ${timestamp} does not exist anymore.`);
    existingBank.prepareForDeletion(logLevel, "already existed but evicted");
    sources[src] = sources[src].filter((bank) => bank !== existingBank);
    const replacementKeybank = await makeKeyframeBank({
      videoSampleSink,
      initialTimestampRequest: timestamp,
      logLevel,
      src
    });
    addKeyframeBank({ src, bank: replacementKeybank });
    return replacementKeybank;
  };
  const requestKeyframeBank = async ({
    timestamp,
    videoSampleSink,
    src,
    logLevel,
    maxCacheSize,
    fps
  }) => {
    ensureToStayUnderMaxCacheSize(logLevel, maxCacheSize);
    clearKeyframeBanksBeforeTime({
      timestampInSeconds: timestamp,
      src,
      logLevel,
      fps
    });
    const keyframeBank = await getKeyframeBankOrRefetch({
      timestamp,
      videoSampleSink,
      src,
      logLevel
    });
    return keyframeBank;
  };
  const clearAll = (logLevel) => {
    const srcs = Object.keys(sources);
    for (const src of srcs) {
      const banks = sources[src];
      for (const bank of banks) {
        bank.prepareForDeletion(logLevel, "clearAll");
      }
      sources[src] = [];
    }
    sources = {};
  };
  let queue = Promise.resolve(undefined);
  return {
    requestKeyframeBank: ({
      timestamp,
      videoSampleSink,
      src,
      logLevel,
      maxCacheSize,
      fps
    }) => {
      queue = queue.then(() => requestKeyframeBank({
        timestamp,
        videoSampleSink,
        src,
        logLevel,
        maxCacheSize,
        fps
      }));
      return queue;
    },
    getCacheStats,
    clearAll
  };
};

// src/caches.ts
var getSafeWindowOfMonotonicity = (fps) => 0.2 * 30 / fps;
var keyframeManager = makeKeyframeManager();
var audioManager = makeAudioManager();
var getTotalCacheStats = () => {
  const keyframeManagerCacheStats = keyframeManager.getCacheStats();
  const audioManagerCacheStats = audioManager.getCacheStats();
  return {
    count: keyframeManagerCacheStats.count + audioManagerCacheStats.count,
    totalSize: keyframeManagerCacheStats.totalSize + audioManagerCacheStats.totalSize
  };
};
var getUncachedMaxCacheSize = (logLevel) => {
  if (typeof window !== "undefined" && window.remotion_mediaCacheSizeInBytes !== undefined && window.remotion_mediaCacheSizeInBytes !== null) {
    if (window.remotion_mediaCacheSizeInBytes < 240 * 1024 * 1024) {
      cancelRender(new Error(`The minimum value for the "mediaCacheSizeInBytes" prop is 240MB (${240 * 1024 * 1024}), got: ${window.remotion_mediaCacheSizeInBytes}`));
    }
    if (window.remotion_mediaCacheSizeInBytes > 20000 * 1024 * 1024) {
      cancelRender(new Error(`The maximum value for the "mediaCacheSizeInBytes" prop is 20GB (${20000 * 1024 * 1024}), got: ${window.remotion_mediaCacheSizeInBytes}`));
    }
    Internals15.Log.verbose({ logLevel, tag: "@remotion/media" }, `Using cache size set using "mediaCacheSizeInBytes": ${(window.remotion_mediaCacheSizeInBytes / 1024 / 1024).toFixed(1)} MB`);
    return window.remotion_mediaCacheSizeInBytes;
  }
  if (typeof window !== "undefined" && window.remotion_initialMemoryAvailable !== undefined && window.remotion_initialMemoryAvailable !== null) {
    const value = window.remotion_initialMemoryAvailable / 2;
    if (value < 500 * 1024 * 1024) {
      Internals15.Log.verbose({ logLevel, tag: "@remotion/media" }, `Using cache size set based on minimum value of 500MB (which is more than half of the available system memory!)`);
      return 500 * 1024 * 1024;
    }
    if (value > 20000 * 1024 * 1024) {
      Internals15.Log.verbose({ logLevel, tag: "@remotion/media" }, `Using cache size set based on maximum value of 20GB (which is less than half of the available system memory)`);
      return 20000 * 1024 * 1024;
    }
    Internals15.Log.verbose({ logLevel, tag: "@remotion/media" }, `Using cache size set based on available memory (50% of available memory): ${(value / 1024 / 1024).toFixed(1)} MB`);
    return value;
  }
  return 1000 * 1000 * 1000;
};
var cachedMaxCacheSize = null;
var getMaxVideoCacheSize = (logLevel) => {
  if (cachedMaxCacheSize !== null) {
    return cachedMaxCacheSize;
  }
  cachedMaxCacheSize = getUncachedMaxCacheSize(logLevel);
  return cachedMaxCacheSize;
};
var useMaxMediaCacheSize = (logLevel) => {
  const context = React2.useContext(Internals15.MaxMediaCacheSizeContext);
  if (context === null) {
    return getMaxVideoCacheSize(logLevel);
  }
  return context;
};

// src/convert-audiodata/apply-volume.ts
var applyVolume = (array, volume) => {
  if (volume === 1) {
    return;
  }
  for (let i = 0;i < array.length; i++) {
    const newValue = array[i] * volume;
    if (newValue < -32768) {
      array[i] = -32768;
    } else if (newValue > 32767) {
      array[i] = 32767;
    } else {
      array[i] = newValue;
    }
  }
};

// src/convert-audiodata/resample-audiodata.ts
var TARGET_NUMBER_OF_CHANNELS = 2;
var TARGET_SAMPLE_RATE = 48000;
var fixFloatingPoint = (value) => {
  if (value % 1 < 0.0000001) {
    return Math.floor(value);
  }
  if (value % 1 > 0.9999999) {
    return Math.ceil(value);
  }
  return value;
};
var resampleAudioData = ({
  srcNumberOfChannels,
  sourceChannels,
  destination,
  targetFrames,
  chunkSize
}) => {
  const getSourceValues = (startUnfixed, endUnfixed, channelIndex) => {
    const start = fixFloatingPoint(startUnfixed);
    const end = fixFloatingPoint(endUnfixed);
    const startFloor = Math.floor(start);
    const startCeil = Math.ceil(start);
    const startFraction = start - startFloor;
    const endFraction = end - Math.floor(end);
    const endFloor = Math.floor(end);
    let weightedSum = 0;
    let totalWeight = 0;
    if (startFraction > 0) {
      const firstSample = sourceChannels[startFloor * srcNumberOfChannels + channelIndex];
      weightedSum += firstSample * (1 - startFraction);
      totalWeight += 1 - startFraction;
    }
    for (let k = startCeil;k < endFloor; k++) {
      const num = sourceChannels[k * srcNumberOfChannels + channelIndex];
      weightedSum += num;
      totalWeight += 1;
    }
    if (endFraction > 0) {
      const lastSample = sourceChannels[endFloor * srcNumberOfChannels + channelIndex];
      weightedSum += lastSample * endFraction;
      totalWeight += endFraction;
    }
    const average = weightedSum / totalWeight;
    return average;
  };
  for (let newFrameIndex = 0;newFrameIndex < targetFrames; newFrameIndex++) {
    const start = newFrameIndex * chunkSize;
    const end = start + chunkSize;
    if (TARGET_NUMBER_OF_CHANNELS === srcNumberOfChannels) {
      for (let i = 0;i < srcNumberOfChannels; i++) {
        destination[newFrameIndex * srcNumberOfChannels + i] = getSourceValues(start, end, i);
      }
    }
    if (srcNumberOfChannels === 1) {
      const m = getSourceValues(start, end, 0);
      destination[newFrameIndex * 2 + 0] = m;
      destination[newFrameIndex * 2 + 1] = m;
    } else if (srcNumberOfChannels === 4) {
      const l = getSourceValues(start, end, 0);
      const r = getSourceValues(start, end, 1);
      const sl = getSourceValues(start, end, 2);
      const sr = getSourceValues(start, end, 3);
      const l2 = 0.5 * (l + sl);
      const r2 = 0.5 * (r + sr);
      destination[newFrameIndex * 2 + 0] = l2;
      destination[newFrameIndex * 2 + 1] = r2;
    } else if (srcNumberOfChannels === 6) {
      const l = getSourceValues(start, end, 0);
      const r = getSourceValues(start, end, 1);
      const c = getSourceValues(start, end, 2);
      const sl = getSourceValues(start, end, 3);
      const sr = getSourceValues(start, end, 4);
      const sq = Math.sqrt(1 / 2);
      const l2 = l + sq * (c + sl);
      const r2 = r + sq * (c + sr);
      destination[newFrameIndex * 2 + 0] = l2;
      destination[newFrameIndex * 2 + 1] = r2;
    } else {
      for (let i = 0;i < srcNumberOfChannels; i++) {
        destination[newFrameIndex * TARGET_NUMBER_OF_CHANNELS + i] = getSourceValues(start, end, i);
      }
    }
  }
};

// src/looped-frame.ts
var frameForVolumeProp = ({
  behavior,
  loop,
  assetDurationInSeconds,
  fps,
  frame,
  startsAt
}) => {
  if (!loop) {
    return frame + startsAt;
  }
  if (behavior === "extend") {
    return frame + startsAt;
  }
  const assetDurationInFrames = Math.floor(assetDurationInSeconds * fps) - startsAt;
  return frame % assetDurationInFrames + startsAt;
};

// src/convert-audiodata/convert-audiodata.ts
var FORMAT = "s16";
var fixFloatingPoint2 = (value) => {
  const decimal = Math.abs(value % 1);
  if (decimal < 0.0000001) {
    return value < 0 ? Math.ceil(value) : Math.floor(value);
  }
  if (decimal > 0.9999999) {
    return value < 0 ? Math.floor(value) : Math.ceil(value);
  }
  return value;
};
var ceilButNotIfFloatingPointIssue = (value) => {
  const fixed = fixFloatingPoint2(value);
  return Math.ceil(fixed);
};
var convertAudioData = ({
  audioData,
  trimStartInSeconds,
  trimEndInSeconds,
  playbackRate,
  audioDataTimestamp,
  isLast
}) => {
  const {
    numberOfChannels: srcNumberOfChannels,
    sampleRate: currentSampleRate,
    numberOfFrames
  } = audioData;
  const ratio = currentSampleRate / TARGET_SAMPLE_RATE;
  const frameOffset = Math.floor(fixFloatingPoint2(trimStartInSeconds * audioData.sampleRate));
  const unroundedFrameCount = numberOfFrames - trimEndInSeconds * audioData.sampleRate - frameOffset;
  const frameCount = isLast ? ceilButNotIfFloatingPointIssue(unroundedFrameCount) : Math.round(unroundedFrameCount);
  const newNumberOfFrames = isLast ? ceilButNotIfFloatingPointIssue(unroundedFrameCount / ratio / playbackRate) : Math.round(unroundedFrameCount / ratio / playbackRate);
  if (newNumberOfFrames === 0) {
    throw new Error("Cannot resample - the given sample rate would result in less than 1 sample");
  }
  const srcChannels = new Int16Array(srcNumberOfChannels * frameCount);
  const isF32 = audioData.format === "f32" || audioData.format === "f32-planar";
  if (isF32) {
    const bytesPerPlane = frameCount * 4;
    const f32Buffer = new ArrayBuffer(srcNumberOfChannels * bytesPerPlane);
    for (let ch = 0;ch < srcNumberOfChannels; ch++) {
      audioData.copyTo(new Float32Array(f32Buffer, ch * bytesPerPlane, frameCount), { planeIndex: ch, frameOffset, frameCount, format: "f32-planar" });
    }
    const f32AudioData = new AudioData({
      format: "f32-planar",
      sampleRate: currentSampleRate,
      numberOfFrames: frameCount,
      numberOfChannels: srcNumberOfChannels,
      timestamp: audioData.timestamp,
      data: f32Buffer
    });
    f32AudioData.copyTo(srcChannels, {
      planeIndex: 0,
      format: FORMAT,
      frameOffset: 0,
      frameCount
    });
    f32AudioData.close();
  } else {
    audioData.copyTo(srcChannels, {
      planeIndex: 0,
      format: FORMAT,
      frameOffset,
      frameCount
    });
  }
  const data = new Int16Array(newNumberOfFrames * TARGET_NUMBER_OF_CHANNELS);
  const chunkSize = frameCount / newNumberOfFrames;
  const timestampOffsetMicroseconds = frameOffset / audioData.sampleRate * 1e6;
  if (newNumberOfFrames === frameCount && TARGET_NUMBER_OF_CHANNELS === srcNumberOfChannels && playbackRate === 1) {
    return {
      data: srcChannels,
      numberOfFrames: newNumberOfFrames,
      timestamp: audioDataTimestamp * 1e6 + fixFloatingPoint2(timestampOffsetMicroseconds),
      durationInMicroSeconds: fixFloatingPoint2(newNumberOfFrames / TARGET_SAMPLE_RATE * 1e6)
    };
  }
  resampleAudioData({
    srcNumberOfChannels,
    sourceChannels: srcChannels,
    destination: data,
    targetFrames: newNumberOfFrames,
    chunkSize
  });
  const newAudioData = {
    data,
    numberOfFrames: newNumberOfFrames,
    timestamp: audioDataTimestamp * 1e6 + fixFloatingPoint2(timestampOffsetMicroseconds),
    durationInMicroSeconds: fixFloatingPoint2(newNumberOfFrames / TARGET_SAMPLE_RATE * 1e6)
  };
  return newAudioData;
};

// src/convert-audiodata/combine-audiodata.ts
var combineAudioDataAndClosePrevious = (audioDataArray) => {
  let numberOfFrames = 0;
  let durationInMicroSeconds = 0;
  const { timestamp } = audioDataArray[0];
  for (const audioData of audioDataArray) {
    numberOfFrames += audioData.numberOfFrames;
    durationInMicroSeconds += audioData.durationInMicroSeconds;
  }
  const arr = new Int16Array(numberOfFrames * TARGET_NUMBER_OF_CHANNELS);
  let offset = 0;
  for (const audioData of audioDataArray) {
    arr.set(audioData.data, offset);
    offset += audioData.data.length;
  }
  return {
    data: arr,
    numberOfFrames,
    timestamp: fixFloatingPoint2(timestamp),
    durationInMicroSeconds: fixFloatingPoint2(durationInMicroSeconds)
  };
};

// src/get-sink.ts
import { Internals as Internals16 } from "remotion";

// src/video-extraction/get-frames-since-keyframe.ts
import {
  ALL_FORMATS as ALL_FORMATS2,
  AudioSampleSink,
  EncodedPacketSink,
  Input as Input2,
  MATROSKA,
  UrlSource as UrlSource2,
  VideoSampleSink,
  WEBM
} from "mediabunny";

// src/browser-can-use-webgl2.ts
var browserCanUseWebGl2 = null;
var browserCanUseWebGl2Uncached = () => {
  const canvas = new OffscreenCanvas(1, 1);
  const context = canvas.getContext("webgl2");
  return context !== null;
};
var canBrowserUseWebGl2 = () => {
  if (browserCanUseWebGl2 !== null) {
    return browserCanUseWebGl2;
  }
  browserCanUseWebGl2 = browserCanUseWebGl2Uncached();
  return browserCanUseWebGl2;
};

// src/video-extraction/remember-actual-matroska-timestamps.ts
var rememberActualMatroskaTimestamps = (isMatroska) => {
  const observations = [];
  const observeTimestamp = (startTime) => {
    if (!isMatroska) {
      return;
    }
    observations.push(startTime);
  };
  const getRealTimestamp = (observedTimestamp) => {
    if (!isMatroska) {
      return observedTimestamp;
    }
    return observations.find((observation) => Math.abs(observedTimestamp - observation) < 0.001) ?? null;
  };
  return {
    observeTimestamp,
    getRealTimestamp
  };
};

// src/video-extraction/get-frames-since-keyframe.ts
var getRetryDelay = () => {
  return null;
};
var getFormatOrNullOrNetworkError = async (input) => {
  try {
    return await input.getFormat();
  } catch (err) {
    if (isNetworkError(err)) {
      return "network-error";
    }
    return null;
  }
};
var getSinks = async (src) => {
  const input = new Input2({
    formats: ALL_FORMATS2,
    source: new UrlSource2(src, {
      getRetryDelay
    })
  });
  const format = await getFormatOrNullOrNetworkError(input);
  const isMatroska = format === MATROSKA || format === WEBM;
  const getVideoSinks = async () => {
    if (format === "network-error") {
      return "network-error";
    }
    if (format === null) {
      return "unknown-container-format";
    }
    const videoTrack = await input.getPrimaryVideoTrack();
    if (!videoTrack) {
      return "no-video-track";
    }
    const canDecode = await videoTrack.canDecode();
    if (!canDecode) {
      return "cannot-decode";
    }
    const sampleSink = new VideoSampleSink(videoTrack);
    const packetSink = new EncodedPacketSink(videoTrack);
    const startPacket = await packetSink.getFirstPacket({
      verifyKeyPackets: true
    });
    const hasAlpha = startPacket?.sideData.alpha;
    if (hasAlpha && !canBrowserUseWebGl2()) {
      return "cannot-decode-alpha";
    }
    return {
      sampleSink
    };
  };
  let videoSinksPromise = null;
  const getVideoSinksPromise = () => {
    if (videoSinksPromise) {
      return videoSinksPromise;
    }
    videoSinksPromise = getVideoSinks();
    return videoSinksPromise;
  };
  const audioSinksPromise = {};
  const getAudioSinks = async (index) => {
    if (format === null) {
      return "unknown-container-format";
    }
    if (format === "network-error") {
      return "network-error";
    }
    const audioTracks = await input.getAudioTracks();
    const audioTrack = audioTracks[index];
    if (!audioTrack) {
      return "no-audio-track";
    }
    const canDecode = await audioTrack.canDecode();
    if (!canDecode) {
      return "cannot-decode-audio";
    }
    return {
      sampleSink: new AudioSampleSink(audioTrack)
    };
  };
  const getAudioSinksPromise = (index) => {
    if (audioSinksPromise[index]) {
      return audioSinksPromise[index];
    }
    audioSinksPromise[index] = getAudioSinks(index);
    return audioSinksPromise[index];
  };
  return {
    getVideo: () => getVideoSinksPromise(),
    getAudio: (index) => getAudioSinksPromise(index),
    actualMatroskaTimestamps: rememberActualMatroskaTimestamps(isMatroska),
    isMatroska,
    getDuration: () => {
      return input.computeDuration();
    }
  };
};

// src/get-sink.ts
var sinkPromises = {};
var getSink = (src, logLevel) => {
  let promise = sinkPromises[src];
  if (!promise) {
    Internals16.Log.verbose({
      logLevel,
      tag: "@remotion/media"
    }, `Sink for ${src} was not found, creating new sink`);
    promise = getSinks(src);
    sinkPromises[src] = promise;
  }
  return promise;
};

// src/audio-extraction/extract-audio.ts
var extractAudioInternal = async ({
  src,
  timeInSeconds: unloopedTimeInSeconds,
  durationInSeconds: durationNotYetApplyingPlaybackRate,
  logLevel,
  loop,
  playbackRate,
  audioStreamIndex,
  trimBefore,
  trimAfter,
  fps,
  maxCacheSize
}) => {
  const { getAudio, actualMatroskaTimestamps, isMatroska, getDuration } = await getSink(src, logLevel);
  let mediaDurationInSeconds = null;
  if (loop) {
    mediaDurationInSeconds = await getDuration();
  }
  const audio = await getAudio(audioStreamIndex);
  if (audio === "network-error") {
    return "network-error";
  }
  if (audio === "no-audio-track") {
    return { data: null, durationInSeconds: null };
  }
  if (audio === "cannot-decode-audio") {
    return "cannot-decode";
  }
  if (audio === "unknown-container-format") {
    return "unknown-container-format";
  }
  const timeInSeconds = getTimeInSeconds({
    loop,
    mediaDurationInSeconds,
    unloopedTimeInSeconds,
    src,
    trimAfter,
    playbackRate,
    trimBefore,
    fps,
    ifNoMediaDuration: "fail"
  });
  if (timeInSeconds === null) {
    return { data: null, durationInSeconds: mediaDurationInSeconds };
  }
  try {
    const sampleIterator = await audioManager.getIterator({
      src,
      timeInSeconds,
      audioSampleSink: audio.sampleSink,
      isMatroska,
      actualMatroskaTimestamps,
      logLevel,
      maxCacheSize
    });
    const durationInSeconds = durationNotYetApplyingPlaybackRate * playbackRate;
    const samples = await sampleIterator.getSamples(timeInSeconds, durationInSeconds);
    audioManager.logOpenFrames();
    const audioDataArray = [];
    for (let i = 0;i < samples.length; i++) {
      const sample = samples[i];
      if (Math.abs(sample.timestamp - (timeInSeconds + durationInSeconds)) * sample.sampleRate < 1) {
        continue;
      }
      if (sample.timestamp + sample.duration <= timeInSeconds) {
        continue;
      }
      const isFirstSample = i === 0;
      const isLastSample = i === samples.length - 1;
      const audioDataRaw = sample.toAudioData();
      let trimStartInSeconds = 0;
      let trimEndInSeconds = 0;
      let leadingSilence = null;
      if (isFirstSample) {
        trimStartInSeconds = fixFloatingPoint2(timeInSeconds - sample.timestamp);
        if (trimStartInSeconds < 0) {
          const silenceFrames = Math.ceil(fixFloatingPoint2(-trimStartInSeconds * TARGET_SAMPLE_RATE));
          leadingSilence = {
            data: new Int16Array(silenceFrames * TARGET_NUMBER_OF_CHANNELS),
            numberOfFrames: silenceFrames,
            timestamp: timeInSeconds * 1e6,
            durationInMicroSeconds: silenceFrames / TARGET_SAMPLE_RATE * 1e6
          };
          trimStartInSeconds = 0;
        }
      }
      if (isLastSample) {
        trimEndInSeconds = Math.max(0, sample.timestamp + sample.duration - (timeInSeconds + durationInSeconds));
      }
      const audioData = convertAudioData({
        audioData: audioDataRaw,
        trimStartInSeconds,
        trimEndInSeconds,
        playbackRate,
        audioDataTimestamp: sample.timestamp,
        isLast: isLastSample
      });
      audioDataRaw.close();
      if (audioData.numberOfFrames === 0) {
        continue;
      }
      if (leadingSilence) {
        audioDataArray.push(leadingSilence);
      }
      audioDataArray.push(audioData);
    }
    if (audioDataArray.length === 0) {
      return { data: null, durationInSeconds: mediaDurationInSeconds };
    }
    const combined = combineAudioDataAndClosePrevious(audioDataArray);
    return { data: combined, durationInSeconds: mediaDurationInSeconds };
  } catch (err) {
    const error = err;
    if (isNetworkError(error)) {
      return "network-error";
    }
    if (isUnsupportedConfigurationError(error)) {
      return "cannot-decode";
    }
    throw err;
  }
};
var queue = Promise.resolve(undefined);
var extractAudio = (params) => {
  queue = queue.then(() => extractAudioInternal(params));
  return queue;
};

// src/video-extraction/extract-frame.ts
import { Internals as Internals17 } from "remotion";
var extractFrameInternal = async ({
  src,
  timeInSeconds: unloopedTimeInSeconds,
  logLevel,
  loop,
  trimAfter,
  trimBefore,
  playbackRate,
  fps,
  maxCacheSize
}) => {
  const sink = await getSink(src, logLevel);
  const [video, mediaDurationInSecondsRaw] = await Promise.all([
    sink.getVideo(),
    loop ? sink.getDuration() : Promise.resolve(null)
  ]);
  const mediaDurationInSeconds = loop ? mediaDurationInSecondsRaw : null;
  if (video === "no-video-track") {
    throw new Error(`No video track found for ${src}`);
  }
  if (video === "cannot-decode") {
    return { type: "cannot-decode", durationInSeconds: mediaDurationInSeconds };
  }
  if (video === "unknown-container-format") {
    return { type: "unknown-container-format" };
  }
  if (video === "network-error") {
    return { type: "network-error" };
  }
  if (video === "cannot-decode-alpha") {
    return {
      type: "cannot-decode-alpha",
      durationInSeconds: mediaDurationInSeconds
    };
  }
  const timeInSeconds = getTimeInSeconds({
    loop,
    mediaDurationInSeconds,
    unloopedTimeInSeconds,
    src,
    trimAfter,
    playbackRate,
    trimBefore,
    fps,
    ifNoMediaDuration: "fail"
  });
  if (timeInSeconds === null) {
    return {
      type: "success",
      frame: null,
      rotation: 0,
      durationInSeconds: await sink.getDuration()
    };
  }
  try {
    const keyframeBank = await keyframeManager.requestKeyframeBank({
      videoSampleSink: video.sampleSink,
      timestamp: timeInSeconds,
      src,
      logLevel,
      maxCacheSize,
      fps
    });
    if (!keyframeBank) {
      return {
        type: "success",
        frame: null,
        rotation: 0,
        durationInSeconds: await sink.getDuration()
      };
    }
    const frame = await keyframeBank.getFrameFromTimestamp(timeInSeconds, fps);
    const rotation = frame?.rotation ?? 0;
    return {
      type: "success",
      frame: frame?.toVideoFrame() ?? null,
      rotation,
      durationInSeconds: await sink.getDuration()
    };
  } catch (err) {
    Internals17.Log.info({ logLevel, tag: "@remotion/media" }, `Error decoding ${src} at time ${timeInSeconds}: ${err}`, err);
    return { type: "cannot-decode", durationInSeconds: mediaDurationInSeconds };
  }
};
var queue2 = Promise.resolve(undefined);
var extractFrame = (params) => {
  queue2 = queue2.then(() => extractFrameInternal(params));
  return queue2;
};

// src/video-extraction/rotate-frame.ts
var rotateFrame = async ({
  frame,
  rotation
}) => {
  if (rotation === 0) {
    const directBitmap = await createImageBitmap(frame);
    frame.close();
    return directBitmap;
  }
  const width = rotation === 90 || rotation === 270 ? frame.displayHeight : frame.displayWidth;
  const height = rotation === 90 || rotation === 270 ? frame.displayWidth : frame.displayHeight;
  const canvas = new OffscreenCanvas(width, height);
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Could not get 2d context");
  }
  canvas.width = width;
  canvas.height = height;
  if (rotation === 90) {
    ctx.translate(width, 0);
  } else if (rotation === 180) {
    ctx.translate(width, height);
  } else if (rotation === 270) {
    ctx.translate(0, height);
  }
  ctx.rotate(rotation * (Math.PI / 180));
  ctx.drawImage(frame, 0, 0);
  const bitmap = await createImageBitmap(canvas);
  frame.close();
  return bitmap;
};

// src/extract-frame-and-audio.ts
var extractFrameAndAudio = async ({
  src,
  timeInSeconds,
  logLevel,
  durationInSeconds,
  playbackRate,
  includeAudio,
  includeVideo,
  loop,
  audioStreamIndex,
  trimAfter,
  trimBefore,
  fps,
  maxCacheSize
}) => {
  try {
    const [video, audio] = await Promise.all([
      includeVideo ? extractFrame({
        src,
        timeInSeconds,
        logLevel,
        loop,
        trimAfter,
        playbackRate,
        trimBefore,
        fps,
        maxCacheSize
      }) : null,
      includeAudio ? extractAudio({
        src,
        timeInSeconds,
        durationInSeconds,
        logLevel,
        loop,
        playbackRate,
        audioStreamIndex,
        trimAfter,
        fps,
        trimBefore,
        maxCacheSize
      }) : null
    ]);
    if (video?.type === "cannot-decode") {
      return {
        type: "cannot-decode",
        durationInSeconds: video.durationInSeconds
      };
    }
    if (video?.type === "unknown-container-format") {
      return { type: "unknown-container-format" };
    }
    if (video?.type === "cannot-decode-alpha") {
      return {
        type: "cannot-decode-alpha",
        durationInSeconds: video.durationInSeconds
      };
    }
    if (video?.type === "network-error") {
      return { type: "network-error" };
    }
    if (audio === "unknown-container-format") {
      return { type: "unknown-container-format" };
    }
    if (audio === "network-error") {
      return { type: "network-error" };
    }
    if (audio === "cannot-decode") {
      return {
        type: "cannot-decode",
        durationInSeconds: video?.type === "success" ? video.durationInSeconds : null
      };
    }
    return {
      type: "success",
      frame: video?.frame ? await rotateFrame({
        frame: video.frame,
        rotation: video.rotation
      }) : null,
      audio: audio?.data ?? null,
      durationInSeconds: audio?.durationInSeconds ?? null
    };
  } catch (err) {
    const error = err;
    if (isNetworkError(error)) {
      return { type: "network-error" };
    }
    throw err;
  }
};

// src/video-extraction/add-broadcast-channel-listener.ts
var emitReadiness = (channel) => {
  channel.postMessage({
    type: "main-tab-ready"
  });
  setInterval(() => {
    channel.postMessage({
      type: "main-tab-ready"
    });
  }, 300);
};
var addBroadcastChannelListener = () => {
  if (!(typeof window !== "undefined" && window.remotion_broadcastChannel && window.remotion_isMainTab)) {
    return;
  }
  window.remotion_broadcastChannel.addEventListener("message", async (event) => {
    const data = event.data;
    if (data.type === "request") {
      try {
        const result = await extractFrameAndAudio({
          src: data.src,
          timeInSeconds: data.timeInSeconds,
          logLevel: data.logLevel,
          durationInSeconds: data.durationInSeconds,
          playbackRate: data.playbackRate,
          includeAudio: data.includeAudio,
          includeVideo: data.includeVideo,
          loop: data.loop,
          audioStreamIndex: data.audioStreamIndex,
          trimAfter: data.trimAfter,
          trimBefore: data.trimBefore,
          fps: data.fps,
          maxCacheSize: data.maxCacheSize
        });
        if (result.type === "cannot-decode") {
          const cannotDecodeResponse = {
            type: "response-cannot-decode",
            id: data.id,
            durationInSeconds: result.durationInSeconds
          };
          window.remotion_broadcastChannel.postMessage(cannotDecodeResponse);
          return;
        }
        if (result.type === "cannot-decode-alpha") {
          const cannotDecodeAlphaResponse = {
            type: "response-cannot-decode-alpha",
            id: data.id,
            durationInSeconds: result.durationInSeconds
          };
          window.remotion_broadcastChannel.postMessage(cannotDecodeAlphaResponse);
          return;
        }
        if (result.type === "network-error") {
          const networkErrorResponse = {
            type: "response-network-error",
            id: data.id
          };
          window.remotion_broadcastChannel.postMessage(networkErrorResponse);
          return;
        }
        if (result.type === "unknown-container-format") {
          const unknownContainerFormatResponse = {
            type: "response-unknown-container-format",
            id: data.id
          };
          window.remotion_broadcastChannel.postMessage(unknownContainerFormatResponse);
          return;
        }
        const { frame, audio, durationInSeconds } = result;
        const imageBitmap = frame ? await createImageBitmap(frame) : null;
        if (frame) {
          frame.close();
        }
        const response = {
          type: "response-success",
          id: data.id,
          frame: imageBitmap,
          audio,
          durationInSeconds: durationInSeconds ?? null
        };
        window.remotion_broadcastChannel.postMessage(response);
      } catch (error) {
        const response = {
          type: "response-error",
          id: data.id,
          errorStack: error.stack ?? "No stack trace"
        };
        window.remotion_broadcastChannel.postMessage(response);
      }
    } else if (data.type === "main-tab-ready") {} else {
      throw new Error("Invalid message: " + JSON.stringify(data));
    }
  });
  emitReadiness(window.remotion_broadcastChannel);
};
var mainTabIsReadyProm = null;
var waitForMainTabToBeReady = (channel) => {
  if (mainTabIsReadyProm) {
    return mainTabIsReadyProm;
  }
  mainTabIsReadyProm = new Promise((resolve) => {
    const onMessage = (event) => {
      const data = event.data;
      if (data.type === "main-tab-ready") {
        resolve();
        channel.removeEventListener("message", onMessage);
      }
    };
    channel.addEventListener("message", onMessage);
  });
  return mainTabIsReadyProm;
};

// src/video-extraction/extract-frame-via-broadcast-channel.ts
addBroadcastChannelListener();
var extractFrameViaBroadcastChannel = async ({
  src,
  timeInSeconds,
  logLevel,
  durationInSeconds,
  playbackRate,
  includeAudio,
  includeVideo,
  isClientSideRendering,
  loop,
  audioStreamIndex,
  trimAfter,
  trimBefore,
  fps,
  maxCacheSize
}) => {
  if (isClientSideRendering || window.remotion_isMainTab) {
    return extractFrameAndAudio({
      logLevel,
      src,
      timeInSeconds,
      durationInSeconds,
      playbackRate,
      includeAudio,
      includeVideo,
      loop,
      audioStreamIndex,
      trimAfter,
      trimBefore,
      fps,
      maxCacheSize
    });
  }
  await waitForMainTabToBeReady(window.remotion_broadcastChannel);
  const requestId = crypto.randomUUID();
  const resolvePromise = new Promise((resolve, reject) => {
    const onMessage = (event) => {
      const data = event.data;
      if (!data) {
        return;
      }
      if (data.type === "main-tab-ready") {
        return;
      }
      if (data.id !== requestId) {
        return;
      }
      if (data.type === "response-success") {
        resolve({
          type: "success",
          frame: data.frame ? data.frame : null,
          audio: data.audio ? data.audio : null,
          durationInSeconds: data.durationInSeconds ? data.durationInSeconds : null
        });
        window.remotion_broadcastChannel.removeEventListener("message", onMessage);
        return;
      }
      if (data.type === "response-error") {
        reject(data.errorStack);
        window.remotion_broadcastChannel.removeEventListener("message", onMessage);
        return;
      }
      if (data.type === "response-cannot-decode") {
        resolve({
          type: "cannot-decode",
          durationInSeconds: data.durationInSeconds
        });
        window.remotion_broadcastChannel.removeEventListener("message", onMessage);
        return;
      }
      if (data.type === "response-network-error") {
        resolve({ type: "network-error" });
        window.remotion_broadcastChannel.removeEventListener("message", onMessage);
        return;
      }
      if (data.type === "response-unknown-container-format") {
        resolve({ type: "unknown-container-format" });
        window.remotion_broadcastChannel.removeEventListener("message", onMessage);
        return;
      }
      if (data.type === "response-cannot-decode-alpha") {
        resolve({
          type: "cannot-decode-alpha",
          durationInSeconds: data.durationInSeconds
        });
        window.remotion_broadcastChannel.removeEventListener("message", onMessage);
        return;
      }
      throw new Error(`Invalid message: ${JSON.stringify(data)}`);
    };
    window.remotion_broadcastChannel.addEventListener("message", onMessage);
  });
  const request = {
    type: "request",
    src,
    timeInSeconds,
    id: requestId,
    logLevel,
    durationInSeconds,
    playbackRate,
    includeAudio,
    includeVideo,
    loop,
    audioStreamIndex,
    trimAfter,
    trimBefore,
    fps,
    maxCacheSize
  };
  window.remotion_broadcastChannel.postMessage(request);
  let timeoutId;
  return Promise.race([
    resolvePromise.then((res) => {
      clearTimeout(timeoutId);
      return res;
    }),
    new Promise((_, reject) => {
      timeoutId = setTimeout(() => {
        reject(new Error(`Timeout while extracting frame at time ${timeInSeconds}sec from ${src}`));
      }, Math.max(3000, window.remotion_puppeteerTimeout - 5000));
    })
  ]);
};

// src/audio/audio-for-rendering.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
var AudioForRendering = ({
  volume: volumeProp,
  playbackRate,
  src,
  muted,
  loopVolumeCurveBehavior,
  delayRenderRetries,
  delayRenderTimeoutInMilliseconds,
  logLevel: overriddenLogLevel,
  loop,
  fallbackHtml5AudioProps,
  audioStreamIndex,
  showInTimeline,
  style,
  name,
  disallowFallbackToHtml5Audio,
  toneFrequency,
  trimAfter,
  trimBefore,
  onError
}) => {
  const defaultLogLevel = Internals18.useLogLevel();
  const logLevel = overriddenLogLevel ?? defaultLogLevel;
  const frame = useCurrentFrame3();
  const absoluteFrame = Internals18.useTimelinePosition();
  const videoConfig = Internals18.useUnsafeVideoConfig();
  const { registerRenderAsset, unregisterRenderAsset } = useContext4(Internals18.RenderAssetManager);
  const startsAt = Internals18.useMediaStartsAt();
  const environment = useRemotionEnvironment();
  if (!videoConfig) {
    throw new Error("No video config found");
  }
  if (!src) {
    throw new TypeError("No `src` was passed to <Audio>.");
  }
  const { fps } = videoConfig;
  const { delayRender, continueRender } = useDelayRender();
  const [replaceWithHtml5Audio, setReplaceWithHtml5Audio] = useState3(false);
  const sequenceContext = useContext4(Internals18.SequenceContext);
  const id = useMemo3(() => `media-audio-${random(src)}-${sequenceContext?.cumulatedFrom}-${sequenceContext?.relativeFrom}-${sequenceContext?.durationInFrames}`, [
    src,
    sequenceContext?.cumulatedFrom,
    sequenceContext?.relativeFrom,
    sequenceContext?.durationInFrames
  ]);
  const maxCacheSize = useMaxMediaCacheSize(logLevel);
  const audioEnabled = Internals18.useAudioEnabled();
  useLayoutEffect2(() => {
    const timestamp = frame / fps;
    const durationInSeconds = 1 / fps;
    const shouldRenderAudio = (() => {
      if (!audioEnabled) {
        return false;
      }
      if (muted) {
        return false;
      }
      return true;
    })();
    if (!shouldRenderAudio) {
      return;
    }
    if (replaceWithHtml5Audio) {
      return;
    }
    const newHandle = delayRender(`Extracting audio for frame ${frame}`, {
      retries: delayRenderRetries ?? undefined,
      timeoutInMilliseconds: delayRenderTimeoutInMilliseconds ?? undefined
    });
    extractFrameViaBroadcastChannel({
      src,
      timeInSeconds: timestamp,
      durationInSeconds,
      playbackRate: playbackRate ?? 1,
      logLevel,
      includeAudio: shouldRenderAudio,
      includeVideo: false,
      isClientSideRendering: environment.isClientSideRendering,
      loop: loop ?? false,
      audioStreamIndex: audioStreamIndex ?? 0,
      trimAfter,
      trimBefore,
      fps,
      maxCacheSize
    }).then((result) => {
      const handleError = (error, clientSideError, fallbackMessage) => {
        const [action, errorToUse] = callOnErrorAndResolve({
          onError,
          error,
          disallowFallback: disallowFallbackToHtml5Audio ?? false,
          isClientSideRendering: environment.isClientSideRendering,
          clientSideError
        });
        if (action === "fail") {
          cancelRender2(errorToUse);
        }
        Internals18.Log.warn({ logLevel, tag: "@remotion/media" }, fallbackMessage);
        setReplaceWithHtml5Audio(true);
      };
      if (result.type === "unknown-container-format") {
        handleError(new Error(`Unknown container format ${src}.`), new Error(`Cannot render audio "${src}": Unknown container format. See supported formats: https://www.remotion.dev/docs/mediabunny/formats`), `Unknown container format for ${src} (Supported formats: https://www.remotion.dev/docs/mediabunny/formats), falling back to <Html5Audio>`);
        return;
      }
      if (result.type === "cannot-decode") {
        handleError(new Error(`Cannot decode ${src}.`), new Error(`Cannot render audio "${src}": The audio could not be decoded by the browser.`), `Cannot decode ${src}, falling back to <Html5Audio>`);
        return;
      }
      if (result.type === "cannot-decode-alpha") {
        throw new Error(`Cannot decode alpha component for ${src}, and 'disallowFallbackToHtml5Audio' was set. But this should never happen, since you used the <Audio> tag. Please report this as a bug.`);
      }
      if (result.type === "network-error") {
        handleError(new Error(`Network error fetching ${src}.`), new Error(`Cannot render audio "${src}": Network error while fetching the audio (possibly CORS).`), `Network error fetching ${src}, falling back to <Html5Audio>`);
        return;
      }
      const { audio, durationInSeconds: assetDurationInSeconds } = result;
      const volumePropsFrame = frameForVolumeProp({
        behavior: loopVolumeCurveBehavior ?? "repeat",
        loop: loop ?? false,
        assetDurationInSeconds: assetDurationInSeconds ?? 0,
        fps,
        frame,
        startsAt
      });
      const volume = Internals18.evaluateVolume({
        volume: volumeProp,
        frame: volumePropsFrame,
        mediaVolume: 1
      });
      Internals18.warnAboutTooHighVolume(volume);
      if (audio && volume > 0) {
        applyVolume(audio.data, volume);
        registerRenderAsset({
          type: "inline-audio",
          id,
          audio: environment.isClientSideRendering ? audio.data : Array.from(audio.data),
          frame: absoluteFrame,
          timestamp: audio.timestamp,
          duration: audio.numberOfFrames / TARGET_SAMPLE_RATE * 1e6,
          toneFrequency: toneFrequency ?? 1
        });
      }
      continueRender(newHandle);
    }).catch((error) => {
      cancelRender2(error);
    });
    return () => {
      continueRender(newHandle);
      unregisterRenderAsset(id);
    };
  }, [
    absoluteFrame,
    continueRender,
    delayRender,
    delayRenderRetries,
    delayRenderTimeoutInMilliseconds,
    disallowFallbackToHtml5Audio,
    environment.isClientSideRendering,
    fps,
    frame,
    id,
    logLevel,
    loop,
    loopVolumeCurveBehavior,
    muted,
    playbackRate,
    registerRenderAsset,
    src,
    startsAt,
    unregisterRenderAsset,
    volumeProp,
    audioStreamIndex,
    toneFrequency,
    trimAfter,
    trimBefore,
    replaceWithHtml5Audio,
    maxCacheSize,
    audioEnabled,
    onError
  ]);
  if (replaceWithHtml5Audio) {
    return /* @__PURE__ */ jsx2(Html5Audio, {
      src,
      playbackRate,
      muted,
      loop,
      volume: volumeProp,
      delayRenderRetries,
      delayRenderTimeoutInMilliseconds,
      style,
      loopVolumeCurveBehavior,
      audioStreamIndex,
      useWebAudioApi: fallbackHtml5AudioProps?.useWebAudioApi,
      onError: fallbackHtml5AudioProps?.onError,
      toneFrequency,
      acceptableTimeShiftInSeconds: fallbackHtml5AudioProps?.acceptableTimeShiftInSeconds,
      name,
      showInTimeline
    });
  }
  return null;
};

// src/audio/audio.tsx
import { jsx as jsx3 } from "react/jsx-runtime";
var { validateMediaProps } = Internals19;
var audioSchema = {
  volume: {
    type: "number",
    min: 0,
    max: 20,
    step: 0.01,
    default: 1,
    description: "Volume"
  },
  playbackRate: {
    type: "number",
    min: 0.1,
    step: 0.01,
    default: 1,
    description: "Playback Rate"
  },
  loop: { type: "boolean", default: false, description: "Loop" }
};
var AudioInner = (props) => {
  const { name, stack, showInTimeline, controls, ...otherProps } = props;
  const environment = useRemotionEnvironment2();
  if (typeof props.src !== "string") {
    throw new TypeError(`The \`<Audio>\` tag requires a string for \`src\`, but got ${JSON.stringify(props.src)} instead.`);
  }
  validateMediaProps({ playbackRate: props.playbackRate, volume: props.volume }, "Audio");
  if (environment.isRendering) {
    return /* @__PURE__ */ jsx3(AudioForRendering, {
      ...otherProps
    });
  }
  return /* @__PURE__ */ jsx3(AudioForPreview, {
    name,
    ...otherProps,
    stack: stack ?? null,
    controls
  });
};
var Audio = Internals19.wrapInSchema(AudioInner, audioSchema);
Internals19.addSequenceStackTraces(Audio);

// src/video/video.tsx
import { Internals as Internals22, useRemotionEnvironment as useRemotionEnvironment4 } from "remotion";

// src/video/video-for-preview.tsx
import {
  useContext as useContext5,
  useEffect as useEffect3,
  useLayoutEffect as useLayoutEffect3,
  useMemo as useMemo4,
  useRef as useRef2,
  useState as useState4
} from "react";
import {
  Html5Video,
  Internals as Internals20,
  useBufferState as useBufferState2,
  useCurrentFrame as useCurrentFrame4,
  useVideoConfig as useVideoConfig3
} from "remotion";
import { jsx as jsx4 } from "react/jsx-runtime";
var {
  useUnsafeVideoConfig: useUnsafeVideoConfig2,
  Timeline: Timeline2,
  SharedAudioContext: SharedAudioContext2,
  useMediaMutedState: useMediaMutedState2,
  useMediaVolumeState: useMediaVolumeState2,
  useFrameForVolumeProp: useFrameForVolumeProp2,
  evaluateVolume: evaluateVolume2,
  warnAboutTooHighVolume: warnAboutTooHighVolume2,
  usePreload: usePreload2,
  SequenceContext: SequenceContext2,
  SequenceVisibilityToggleContext
} = Internals20;
var VideoForPreviewAssertedShowing = ({
  src: unpreloadedSrc,
  style,
  playbackRate,
  logLevel,
  className,
  muted,
  volume,
  loopVolumeCurveBehavior,
  onVideoFrame,
  showInTimeline,
  loop,
  name,
  trimAfter,
  trimBefore,
  stack,
  disallowFallbackToOffthreadVideo,
  fallbackOffthreadVideoProps,
  audioStreamIndex,
  debugOverlay,
  debugAudioScheduling,
  headless,
  onError,
  controls
}) => {
  const src = usePreload2(unpreloadedSrc);
  const canvasRef = useRef2(null);
  const videoConfig = useUnsafeVideoConfig2();
  const frame = useCurrentFrame4();
  const mediaPlayerRef = useRef2(null);
  const initialTrimBeforeRef = useRef2(trimBefore);
  const initialTrimAfterRef = useRef2(trimAfter);
  const initialOnVideoFrameRef = useRef2(onVideoFrame);
  const [mediaPlayerReady, setMediaPlayerReady] = useState4(false);
  const [shouldFallbackToNativeVideo, setShouldFallbackToNativeVideo] = useState4(false);
  const [playing] = Timeline2.usePlayingState();
  const timelineContext = Internals20.useTimelineContext();
  const globalPlaybackRate = timelineContext.playbackRate;
  const sharedAudioContext = useContext5(SharedAudioContext2);
  const buffer = useBufferState2();
  const [mediaMuted] = useMediaMutedState2();
  const [mediaVolume] = useMediaVolumeState2();
  const [mediaDurationInSeconds, setMediaDurationInSeconds] = useState4(null);
  const { hidden } = useContext5(SequenceVisibilityToggleContext);
  const volumePropFrame = useFrameForVolumeProp2(loopVolumeCurveBehavior);
  const userPreferredVolume = evaluateVolume2({
    frame: volumePropFrame,
    volume,
    mediaVolume
  });
  if (!videoConfig) {
    throw new Error("No video config found");
  }
  warnAboutTooHighVolume2(userPreferredVolume);
  const parentSequence = useContext5(SequenceContext2);
  const isPremounting = Boolean(parentSequence?.premounting);
  const isPostmounting = Boolean(parentSequence?.postmounting);
  const sequenceOffset = ((parentSequence?.cumulatedFrom ?? 0) + (parentSequence?.relativeFrom ?? 0)) / videoConfig.fps;
  const loopDisplay = useLoopDisplay({
    loop,
    mediaDurationInSeconds,
    playbackRate,
    trimAfter,
    trimBefore
  });
  const { id: timelineId } = useMediaInTimeline({
    volume,
    mediaType: "video",
    src,
    playbackRate,
    displayName: name ?? null,
    stack,
    showInTimeline,
    premountDisplay: parentSequence?.premountDisplay ?? null,
    postmountDisplay: parentSequence?.postmountDisplay ?? null,
    loopDisplay,
    mediaVolume,
    trimAfter,
    trimBefore,
    controls
  });
  const isSequenceHidden = hidden[timelineId] ?? false;
  const currentTime = frame / videoConfig.fps;
  const currentTimeRef = useRef2(currentTime);
  currentTimeRef.current = currentTime;
  const preloadedSrc = usePreload2(src);
  const buffering = useContext5(Internals20.BufferingContextReact);
  if (!buffering) {
    throw new Error("useMediaPlayback must be used inside a <BufferingContext>");
  }
  const effectiveMuted = isSequenceHidden || muted || mediaMuted || userPreferredVolume <= 0;
  const isPlayerBuffering = Internals20.useIsPlayerBuffering(buffering);
  const initialPlaying = useRef2(playing && !isPlayerBuffering);
  const initialIsPremounting = useRef2(isPremounting);
  const initialIsPostmounting = useRef2(isPostmounting);
  const initialGlobalPlaybackRate = useRef2(globalPlaybackRate);
  const initialPlaybackRate = useRef2(playbackRate);
  const initialMuted = useRef2(effectiveMuted);
  const initialSequenceOffset = useRef2(sequenceOffset);
  useEffect3(() => {
    if (!sharedAudioContext)
      return;
    if (!sharedAudioContext.audioContext)
      return;
    const { audioContext, audioSyncAnchor, scheduleAudioNode } = sharedAudioContext;
    try {
      const player = new MediaPlayer({
        canvas: canvasRef.current,
        src: preloadedSrc,
        logLevel,
        sharedAudioContext: { audioContext, audioSyncAnchor, scheduleAudioNode },
        loop,
        trimAfter: initialTrimAfterRef.current,
        trimBefore: initialTrimBeforeRef.current,
        fps: videoConfig.fps,
        playbackRate: initialPlaybackRate.current,
        audioStreamIndex,
        debugOverlay,
        debugAudioScheduling,
        bufferState: buffer,
        isPremounting: initialIsPremounting.current,
        isPostmounting: initialIsPostmounting.current,
        globalPlaybackRate: initialGlobalPlaybackRate.current,
        durationInFrames: videoConfig.durationInFrames,
        onVideoFrameCallback: initialOnVideoFrameRef.current ?? null,
        playing: initialPlaying.current,
        sequenceOffset: initialSequenceOffset.current
      });
      mediaPlayerRef.current = player;
      player.initialize(currentTimeRef.current, initialMuted.current).then((result) => {
        if (result.type === "disposed") {
          return;
        }
        const handleError = (error, fallbackMessage) => {
          const [action, errorToUse] = callOnErrorAndResolve({
            onError,
            error,
            disallowFallback: disallowFallbackToOffthreadVideo,
            isClientSideRendering: false,
            clientSideError: error
          });
          if (action === "fail") {
            throw errorToUse;
          }
          Internals20.Log.warn({ logLevel, tag: "@remotion/media" }, fallbackMessage);
          setShouldFallbackToNativeVideo(true);
        };
        if (result.type === "unknown-container-format") {
          handleError(new Error(`Unknown container format ${preloadedSrc}.`), `Unknown container format for ${preloadedSrc} (Supported formats: https://www.remotion.dev/docs/mediabunny/formats), falling back to <OffthreadVideo>`);
          return;
        }
        if (result.type === "network-error") {
          handleError(new Error(`Network error fetching ${preloadedSrc}.`), `Network error fetching ${preloadedSrc}, falling back to <OffthreadVideo>`);
          return;
        }
        if (result.type === "cannot-decode") {
          handleError(new Error(`Cannot decode ${preloadedSrc}.`), `Cannot decode ${preloadedSrc}, falling back to <OffthreadVideo>`);
          return;
        }
        if (result.type === "no-tracks") {
          handleError(new Error(`No video or audio tracks found for ${preloadedSrc}.`), `No video or audio tracks found for ${preloadedSrc}, falling back to <OffthreadVideo>`);
          return;
        }
        if (result.type === "success") {
          setMediaPlayerReady(true);
          setMediaDurationInSeconds(result.durationInSeconds);
        }
      }).catch((error) => {
        const [action, errorToUse] = callOnErrorAndResolve({
          onError,
          error,
          disallowFallback: disallowFallbackToOffthreadVideo,
          isClientSideRendering: false,
          clientSideError: error
        });
        if (action === "fail") {
          throw errorToUse;
        }
        Internals20.Log.error({ logLevel, tag: "@remotion/media" }, "[VideoForPreview] Failed to initialize MediaPlayer", errorToUse);
        setShouldFallbackToNativeVideo(true);
      });
    } catch (error) {
      const [action, errorToUse] = callOnErrorAndResolve({
        error,
        onError,
        disallowFallback: disallowFallbackToOffthreadVideo,
        isClientSideRendering: false,
        clientSideError: error
      });
      if (action === "fail") {
        throw errorToUse;
      }
      Internals20.Log.error({ logLevel, tag: "@remotion/media" }, "[VideoForPreview] MediaPlayer initialization failed", errorToUse);
      setShouldFallbackToNativeVideo(true);
    }
    return () => {
      if (mediaPlayerRef.current) {
        Internals20.Log.trace({ logLevel, tag: "@remotion/media" }, `[VideoForPreview] Disposing MediaPlayer`);
        mediaPlayerRef.current.dispose();
        mediaPlayerRef.current = null;
      }
      setMediaPlayerReady(false);
      setShouldFallbackToNativeVideo(false);
    };
  }, [
    audioStreamIndex,
    buffer,
    debugOverlay,
    debugAudioScheduling,
    disallowFallbackToOffthreadVideo,
    logLevel,
    loop,
    preloadedSrc,
    sharedAudioContext,
    videoConfig.fps,
    onError,
    videoConfig.durationInFrames
  ]);
  const classNameValue = useMemo4(() => {
    return [Internals20.OBJECTFIT_CONTAIN_CLASS_NAME, className].filter(Internals20.truthy).join(" ");
  }, [className]);
  useCommonEffects({
    mediaPlayerRef,
    mediaPlayerReady,
    currentTimeRef,
    playing,
    isPlayerBuffering,
    frame,
    trimBefore,
    trimAfter,
    effectiveMuted,
    userPreferredVolume,
    playbackRate,
    globalPlaybackRate,
    fps: videoConfig.fps,
    sequenceOffset,
    loop,
    debugAudioScheduling,
    durationInFrames: videoConfig.durationInFrames,
    isPremounting,
    isPostmounting,
    currentTime,
    logLevel,
    sharedAudioContext,
    label: "VideoForPreview"
  });
  useLayoutEffect3(() => {
    const mediaPlayer = mediaPlayerRef.current;
    if (!mediaPlayer || !mediaPlayerReady) {
      return;
    }
    mediaPlayer.setDebugOverlay(debugOverlay);
  }, [debugOverlay, mediaPlayerReady]);
  useLayoutEffect3(() => {
    const mediaPlayer = mediaPlayerRef.current;
    if (!mediaPlayer || !mediaPlayerReady) {
      return;
    }
    mediaPlayer.setVideoFrameCallback(onVideoFrame ?? null);
  }, [onVideoFrame, mediaPlayerReady]);
  const actualStyle = useMemo4(() => {
    return {
      ...style,
      opacity: isSequenceHidden ? 0 : style?.opacity ?? 1
    };
  }, [isSequenceHidden, style]);
  if (shouldFallbackToNativeVideo && !disallowFallbackToOffthreadVideo) {
    return /* @__PURE__ */ jsx4(Html5Video, {
      src,
      style: actualStyle,
      className,
      muted,
      volume,
      trimAfter,
      trimBefore,
      playbackRate,
      loopVolumeCurveBehavior,
      name,
      loop,
      showInTimeline,
      stack: stack ?? undefined,
      ...fallbackOffthreadVideoProps
    });
  }
  if (headless) {
    return null;
  }
  return /* @__PURE__ */ jsx4("canvas", {
    ref: canvasRef,
    style: actualStyle,
    className: classNameValue
  });
};
var VideoForPreview = (props) => {
  const frame = useCurrentFrame4();
  const videoConfig = useVideoConfig3();
  const currentTime = frame / videoConfig.fps;
  const showShow = useMemo4(() => {
    return getTimeInSeconds({
      unloopedTimeInSeconds: currentTime,
      playbackRate: props.playbackRate,
      loop: props.loop,
      trimBefore: props.trimBefore,
      trimAfter: props.trimAfter,
      mediaDurationInSeconds: Infinity,
      fps: videoConfig.fps,
      ifNoMediaDuration: "infinity",
      src: props.src
    }) !== null;
  }, [
    currentTime,
    props.loop,
    props.playbackRate,
    props.src,
    videoConfig.fps,
    props.trimBefore,
    props.trimAfter
  ]);
  if (!showShow) {
    return null;
  }
  return /* @__PURE__ */ jsx4(VideoForPreviewAssertedShowing, {
    ...props,
    controls: props.controls
  });
};

// src/video/video-for-rendering.tsx
import {
  useContext as useContext6,
  useLayoutEffect as useLayoutEffect4,
  useMemo as useMemo5,
  useRef as useRef3,
  useState as useState5
} from "react";
import {
  Internals as Internals21,
  Loop,
  random as random2,
  useCurrentFrame as useCurrentFrame5,
  useDelayRender as useDelayRender2,
  useRemotionEnvironment as useRemotionEnvironment3,
  useVideoConfig as useVideoConfig4
} from "remotion";
import { jsx as jsx5 } from "react/jsx-runtime";
var VideoForRendering = ({
  volume: volumeProp,
  playbackRate,
  src,
  muted,
  loopVolumeCurveBehavior,
  delayRenderRetries,
  delayRenderTimeoutInMilliseconds,
  onVideoFrame,
  logLevel,
  loop,
  style,
  className,
  fallbackOffthreadVideoProps,
  audioStreamIndex,
  name,
  disallowFallbackToOffthreadVideo,
  stack,
  toneFrequency,
  trimAfterValue,
  trimBeforeValue,
  headless,
  onError
}) => {
  if (!src) {
    throw new TypeError("No `src` was passed to <Video>.");
  }
  const frame = useCurrentFrame5();
  const absoluteFrame = Internals21.useTimelinePosition();
  const { fps } = useVideoConfig4();
  const { registerRenderAsset, unregisterRenderAsset } = useContext6(Internals21.RenderAssetManager);
  const startsAt = Internals21.useMediaStartsAt();
  const sequenceContext = useContext6(Internals21.SequenceContext);
  const id = useMemo5(() => `media-video-${random2(src)}-${sequenceContext?.cumulatedFrom}-${sequenceContext?.relativeFrom}-${sequenceContext?.durationInFrames}`, [
    src,
    sequenceContext?.cumulatedFrom,
    sequenceContext?.relativeFrom,
    sequenceContext?.durationInFrames
  ]);
  const environment = useRemotionEnvironment3();
  const { delayRender, continueRender, cancelRender: cancelRender3 } = useDelayRender2();
  const canvasRef = useRef3(null);
  const [replaceWithOffthreadVideo, setReplaceWithOffthreadVideo] = useState5(false);
  const audioEnabled = Internals21.useAudioEnabled();
  const videoEnabled = Internals21.useVideoEnabled();
  const maxCacheSize = useMaxMediaCacheSize(logLevel);
  const [error, setError] = useState5(null);
  if (error) {
    throw error;
  }
  useLayoutEffect4(() => {
    if (!canvasRef.current && !headless) {
      return;
    }
    if (replaceWithOffthreadVideo) {
      return;
    }
    if (!canvasRef.current?.getContext && !headless) {
      return setError(new Error("Canvas does not have .getContext() method available. This could be because <Video> was mounted inside an <svg> tag."));
    }
    const timestamp = frame / fps;
    const durationInSeconds = 1 / fps;
    const newHandle = delayRender(`Extracting frame at time ${timestamp} from ${src}`, {
      retries: delayRenderRetries ?? undefined,
      timeoutInMilliseconds: delayRenderTimeoutInMilliseconds ?? undefined
    });
    const shouldRenderAudio = (() => {
      if (!audioEnabled) {
        return false;
      }
      if (muted) {
        return false;
      }
      return true;
    })();
    extractFrameViaBroadcastChannel({
      src,
      timeInSeconds: timestamp,
      durationInSeconds,
      playbackRate,
      logLevel,
      includeAudio: shouldRenderAudio,
      includeVideo: videoEnabled,
      isClientSideRendering: environment.isClientSideRendering,
      loop,
      audioStreamIndex,
      trimAfter: trimAfterValue,
      trimBefore: trimBeforeValue,
      fps,
      maxCacheSize
    }).then((result) => {
      const handleError = (err, clientSideError, fallbackMessage, mediaDurationInSeconds) => {
        if (environment.isClientSideRendering) {
          cancelRender3(clientSideError);
          return;
        }
        const [action, errorToUse] = callOnErrorAndResolve({
          onError,
          error: err,
          disallowFallback: disallowFallbackToOffthreadVideo,
          isClientSideRendering: environment.isClientSideRendering,
          clientSideError: err
        });
        if (action === "fail") {
          cancelRender3(errorToUse);
          return;
        }
        if (window.remotion_isMainTab) {
          Internals21.Log.warn({ logLevel, tag: "@remotion/media" }, fallbackMessage);
        }
        setReplaceWithOffthreadVideo({
          durationInSeconds: mediaDurationInSeconds
        });
      };
      if (result.type === "unknown-container-format") {
        handleError(new Error(`Unknown container format ${src}.`), new Error(`Cannot render video "${src}": Unknown container format. See supported formats: https://www.remotion.dev/docs/mediabunny/formats`), `Unknown container format for ${src} (Supported formats: https://www.remotion.dev/docs/mediabunny/formats), falling back to <OffthreadVideo>`, null);
        return;
      }
      if (result.type === "cannot-decode") {
        handleError(new Error(`Cannot decode ${src}.`), new Error(`Cannot render video "${src}": The video could not be decoded by the browser.`), `Cannot decode ${src}, falling back to <OffthreadVideo>`, result.durationInSeconds);
        return;
      }
      if (result.type === "cannot-decode-alpha") {
        handleError(new Error(`Cannot decode alpha component for ${src}.`), new Error(`Cannot render video "${src}": The alpha channel could not be decoded by the browser.`), `Cannot decode alpha component for ${src}, falling back to <OffthreadVideo>`, result.durationInSeconds);
        return;
      }
      if (result.type === "network-error") {
        handleError(new Error(`Network error fetching ${src}.`), new Error(`Cannot render video "${src}": Network error while fetching the video (possibly CORS).`), `Network error fetching ${src} (no CORS?), falling back to <OffthreadVideo>`, null);
        return;
      }
      const {
        frame: imageBitmap,
        audio,
        durationInSeconds: assetDurationInSeconds
      } = result;
      if (imageBitmap) {
        onVideoFrame?.(imageBitmap);
        const context = canvasRef.current?.getContext("2d", {
          alpha: true
        });
        if (context) {
          context.canvas.width = imageBitmap.width;
          context.canvas.height = imageBitmap.height;
          context.canvas.style.aspectRatio = `${context.canvas.width} / ${context.canvas.height}`;
          context.drawImage(imageBitmap, 0, 0);
        }
        imageBitmap.close();
      } else if (videoEnabled) {
        const context = canvasRef.current?.getContext("2d", {
          alpha: true
        });
        if (context) {
          context.clearRect(0, 0, context.canvas.width, context.canvas.height);
        }
      }
      const volumePropsFrame = frameForVolumeProp({
        behavior: loopVolumeCurveBehavior,
        loop,
        assetDurationInSeconds: assetDurationInSeconds ?? 0,
        fps,
        frame,
        startsAt
      });
      const volume = Internals21.evaluateVolume({
        volume: volumeProp,
        frame: volumePropsFrame,
        mediaVolume: 1
      });
      Internals21.warnAboutTooHighVolume(volume);
      if (audio && volume > 0) {
        applyVolume(audio.data, volume);
        registerRenderAsset({
          type: "inline-audio",
          id,
          audio: environment.isClientSideRendering ? audio.data : Array.from(audio.data),
          frame: absoluteFrame,
          timestamp: audio.timestamp,
          duration: audio.numberOfFrames / TARGET_SAMPLE_RATE * 1e6,
          toneFrequency
        });
      }
      continueRender(newHandle);
    }).catch((err) => {
      cancelRender3(err);
    });
    return () => {
      continueRender(newHandle);
      unregisterRenderAsset(id);
    };
  }, [
    absoluteFrame,
    continueRender,
    delayRender,
    delayRenderRetries,
    delayRenderTimeoutInMilliseconds,
    environment.isClientSideRendering,
    fps,
    frame,
    id,
    logLevel,
    loop,
    loopVolumeCurveBehavior,
    muted,
    onVideoFrame,
    playbackRate,
    registerRenderAsset,
    src,
    startsAt,
    unregisterRenderAsset,
    volumeProp,
    replaceWithOffthreadVideo,
    audioStreamIndex,
    disallowFallbackToOffthreadVideo,
    toneFrequency,
    trimAfterValue,
    trimBeforeValue,
    audioEnabled,
    videoEnabled,
    maxCacheSize,
    cancelRender3,
    headless,
    onError
  ]);
  const classNameValue = useMemo5(() => {
    return [Internals21.OBJECTFIT_CONTAIN_CLASS_NAME, className].filter(Internals21.truthy).join(" ");
  }, [className]);
  if (replaceWithOffthreadVideo) {
    const fallback = /* @__PURE__ */ jsx5(Internals21.InnerOffthreadVideo, {
      src,
      playbackRate: playbackRate ?? 1,
      muted: muted ?? false,
      acceptableTimeShiftInSeconds: fallbackOffthreadVideoProps?.acceptableTimeShiftInSeconds,
      loopVolumeCurveBehavior: loopVolumeCurveBehavior ?? "repeat",
      delayRenderRetries: delayRenderRetries ?? undefined,
      delayRenderTimeoutInMilliseconds: delayRenderTimeoutInMilliseconds ?? undefined,
      style,
      allowAmplificationDuringRender: true,
      transparent: fallbackOffthreadVideoProps?.transparent ?? true,
      toneMapped: fallbackOffthreadVideoProps?.toneMapped ?? true,
      audioStreamIndex: audioStreamIndex ?? 0,
      name,
      className,
      onVideoFrame,
      volume: volumeProp,
      id,
      onError: fallbackOffthreadVideoProps?.onError,
      toneFrequency,
      showInTimeline: false,
      crossOrigin: fallbackOffthreadVideoProps?.crossOrigin,
      onAutoPlayError: fallbackOffthreadVideoProps?.onAutoPlayError ?? null,
      pauseWhenBuffering: fallbackOffthreadVideoProps?.pauseWhenBuffering ?? false,
      trimAfter: trimAfterValue,
      trimBefore: trimBeforeValue,
      useWebAudioApi: fallbackOffthreadVideoProps?.useWebAudioApi ?? false,
      startFrom: undefined,
      endAt: undefined,
      stack,
      _remotionInternalNativeLoopPassed: false
    });
    if (loop) {
      if (!replaceWithOffthreadVideo.durationInSeconds) {
        const err = new Error(`Cannot render video ${src}: @remotion/media was unable to render, and fell back to <OffthreadVideo>. Also, "loop" was set, but <OffthreadVideo> does not support looping and @remotion/media could also not determine the duration of the video.`);
        cancelRender3(err);
        throw err;
      }
      return /* @__PURE__ */ jsx5(Loop, {
        layout: "none",
        durationInFrames: Internals21.calculateMediaDuration({
          trimAfter: trimAfterValue,
          mediaDurationInFrames: replaceWithOffthreadVideo.durationInSeconds * fps,
          playbackRate,
          trimBefore: trimBeforeValue
        }),
        children: fallback
      });
    }
    return fallback;
  }
  if (headless) {
    return null;
  }
  return /* @__PURE__ */ jsx5("canvas", {
    ref: canvasRef,
    style,
    className: classNameValue
  });
};

// src/video/video.tsx
import { jsx as jsx6 } from "react/jsx-runtime";
var { validateMediaTrimProps, resolveTrimProps, validateMediaProps: validateMediaProps2 } = Internals22;
var videoSchema = {
  volume: {
    type: "number",
    min: 0,
    max: 20,
    step: 0.01,
    default: 1,
    description: "Volume"
  },
  playbackRate: {
    type: "number",
    min: 0.1,
    step: 0.01,
    default: 1,
    description: "Playback Rate"
  },
  loop: { type: "boolean", default: false, description: "Loop" },
  "style.translate": {
    type: "translate",
    step: 1,
    default: "0px 0px",
    description: "Position"
  },
  "style.scale": {
    type: "number",
    min: 0.05,
    max: 100,
    step: 0.01,
    default: 1,
    description: "Scale"
  },
  "style.rotate": {
    type: "rotation",
    step: 1,
    default: "0deg",
    description: "Rotation"
  },
  "style.opacity": {
    type: "number",
    min: 0,
    max: 1,
    step: 0.01,
    default: 1,
    description: "Opacity"
  }
};
var InnerVideo = ({
  src,
  audioStreamIndex,
  className,
  delayRenderRetries,
  delayRenderTimeoutInMilliseconds,
  disallowFallbackToOffthreadVideo,
  fallbackOffthreadVideoProps,
  logLevel,
  loop,
  loopVolumeCurveBehavior,
  muted,
  name,
  onVideoFrame,
  playbackRate,
  style,
  trimAfter,
  trimBefore,
  volume,
  stack,
  toneFrequency,
  showInTimeline,
  debugOverlay,
  debugAudioScheduling,
  headless,
  onError,
  controls
}) => {
  const environment = useRemotionEnvironment4();
  if (typeof src !== "string") {
    throw new TypeError(`The \`<Video>\` tag requires a string for \`src\`, but got ${JSON.stringify(src)} instead.`);
  }
  validateMediaTrimProps({
    startFrom: undefined,
    endAt: undefined,
    trimBefore,
    trimAfter
  });
  const { trimBeforeValue, trimAfterValue } = resolveTrimProps({
    startFrom: undefined,
    endAt: undefined,
    trimBefore,
    trimAfter
  });
  validateMediaProps2({ playbackRate, volume }, "Video");
  if (environment.isRendering) {
    return /* @__PURE__ */ jsx6(VideoForRendering, {
      audioStreamIndex: audioStreamIndex ?? 0,
      className,
      delayRenderRetries: delayRenderRetries ?? null,
      delayRenderTimeoutInMilliseconds: delayRenderTimeoutInMilliseconds ?? null,
      disallowFallbackToOffthreadVideo: disallowFallbackToOffthreadVideo ?? false,
      name,
      fallbackOffthreadVideoProps,
      logLevel,
      loop,
      loopVolumeCurveBehavior,
      muted,
      onVideoFrame,
      playbackRate,
      src,
      stack,
      style,
      volume,
      toneFrequency,
      trimAfterValue,
      trimBeforeValue,
      headless,
      onError
    });
  }
  return /* @__PURE__ */ jsx6(VideoForPreview, {
    audioStreamIndex: audioStreamIndex ?? 0,
    className,
    name,
    logLevel,
    loop,
    loopVolumeCurveBehavior,
    muted,
    onVideoFrame,
    playbackRate,
    src,
    style,
    volume,
    showInTimeline,
    trimAfter: trimAfterValue,
    trimBefore: trimBeforeValue,
    stack: stack ?? null,
    disallowFallbackToOffthreadVideo,
    fallbackOffthreadVideoProps,
    debugOverlay: debugOverlay ?? false,
    debugAudioScheduling: debugAudioScheduling ?? false,
    headless: headless ?? false,
    onError,
    controls
  });
};
var VideoInner = ({
  src,
  audioStreamIndex,
  className,
  delayRenderRetries,
  delayRenderTimeoutInMilliseconds,
  disallowFallbackToOffthreadVideo,
  fallbackOffthreadVideoProps,
  logLevel,
  loop,
  loopVolumeCurveBehavior,
  muted,
  name,
  onVideoFrame,
  playbackRate,
  showInTimeline,
  style,
  trimAfter,
  trimBefore,
  volume,
  stack,
  toneFrequency,
  debugOverlay,
  debugAudioScheduling,
  headless,
  onError,
  controls
}) => {
  const fallbackLogLevel = Internals22.useLogLevel();
  return /* @__PURE__ */ jsx6(InnerVideo, {
    audioStreamIndex: audioStreamIndex ?? 0,
    className,
    delayRenderRetries: delayRenderRetries ?? null,
    delayRenderTimeoutInMilliseconds: delayRenderTimeoutInMilliseconds ?? null,
    disallowFallbackToOffthreadVideo: disallowFallbackToOffthreadVideo ?? false,
    fallbackOffthreadVideoProps: fallbackOffthreadVideoProps ?? {},
    logLevel: logLevel ?? fallbackLogLevel,
    loop: loop ?? false,
    loopVolumeCurveBehavior: loopVolumeCurveBehavior ?? "repeat",
    muted: muted ?? false,
    name,
    onVideoFrame,
    playbackRate: playbackRate ?? 1,
    showInTimeline: showInTimeline ?? true,
    src,
    style: style ?? {},
    trimAfter,
    trimBefore,
    volume: volume ?? 1,
    toneFrequency: toneFrequency ?? 1,
    stack,
    debugOverlay: debugOverlay ?? false,
    debugAudioScheduling: debugAudioScheduling ?? false,
    headless: headless ?? false,
    onError,
    controls
  });
};
var Video = Internals22.wrapInSchema(VideoInner, videoSchema);
Internals22.addSequenceStackTraces(Video);

// src/index.ts
var experimental_Audio = Audio;
var experimental_Video = Video;
export {
  experimental_Video,
  experimental_Audio,
  Video,
  AudioForPreview,
  Audio
};
